import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ChatService } from 'src/app/services/chat.service';
import { IdentityService } from 'src/app/services/identity.service';
import { ConfigService } from 'src/app/services/config.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { debounce } from 'src/app/directives/debounce.decorator';
import { SocketService } from 'src/app/services/socket.service';
import { ChatScreenComponent } from '../../chat-screen/chat-screen.component';
import { ChatInputComponent, ChatInputSubmitEvent } from '../../chat-input/chat-input.component';
import { WidgetService } from 'src/app/services/widget.service';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DefaultChat, SettingsService } from 'src/app/services/settings.service';
import { ModelChartWidgetComponent } from '../model-chart-widget/model-chart-widget.component';
import { StreamWidgetComponent } from '../stream-widget/stream-widget.component';
import { DefinitionsWidgetComponent } from '../definitions-widget/definitions-widget.component';
import { LLMWorkflowService } from 'src/app/services/llmworkflow.service';
import { DomSanitizer } from '@angular/platform-browser';
import { VersionService } from 'src/app/services/version.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MuxAgentService } from 'src/app/services/mux-agent.service';
import { MuxSessionService } from 'src/app/services/mux-session.service';
import { MuxStreamService } from 'src/app/services/mux-stream.service';
import { MuxUsageService } from 'src/app/services/mux-usage.service';
import { DesktopStateService } from 'src/app/services/desktop-state.service';
import { WidgetContextService } from 'src/app/services/widget-context.service';
import { ClaudeAuthWizardService } from 'src/app/services/claude-auth-wizard.service';
import { TeamsService } from 'src/app/services/teams.service';
import { UserPrefsService } from 'src/app/services/user-prefs.service';
import { DynamicWidgetsService, DynamicWidget } from 'src/app/services/dynamic-widgets.service';
import { DialogService } from '../../../services/dialog.service';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

type Sample = {
  icon: string;
  text: string;
};

/** Represents an open app in the app pane */
export interface OpenApp {
  id: string;
  name: string;
  widgetName: WidgetService.WidgetName;
  icon: string;
  customInputs?: Record<string, any>;  // Extra inputs for the widget
}

@Component({
    selector: 'app-chat-widget',
    templateUrl: './chat-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './chat-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MAT_SELECT_CONFIG,
            useValue: { overlayPanelClass: 'model-selector-overlay-pane' }
        }
    ],
    host: {
        '[attr.isfullscreen]': 'isFullScreen' // Add a host attribute binding
    },
    standalone: false
})
export class ChatWidgetComponent extends BaseWidgetComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private dialogs = inject(DialogService);
  // Performance monitoring: log when CD takes >5ms
  ngDoCheck(): void {
    performance.mark('chat-widget-cd-start');
  }

  private _resizeObserver: ResizeObserver | null = null;

  ngAfterViewInit(): void {
    // Set initial content padding to match the default input area height.
    setTimeout(() => this.onInputResized(), 0);

    // cdkTextareaAutosize caches the placeholder height on first measurement.
    // In dockview, the textarea may have no width constraint during init, causing
    // the placeholder to wrap and produce a huge cached height (e.g. 1104px).
    // Fix: observe the textarea's container for resize — when dockview assigns a
    // real width, invalidate the cache and re-measure.
    this._fixTextareaAutosize();
  }

  /** Invalidate cdkTextareaAutosize placeholder cache and re-measure */
  private _fixTextareaAutosize(): void {
    const ta = this.chatInputComponent?.chatInput?.nativeElement as HTMLTextAreaElement;
    if (!ta) return;

    const fix = () => {
      const autosize = this.chatInputComponent?.autosize as any;
      if (!autosize) return;
      autosize._cachedPlaceholderHeight = undefined;
      autosize.resizeToFitContent(true);
      this.onInputResized();
    };

    // Use ResizeObserver: fires when dockview lays out the panel and the
    // textarea gets its real width. This is the correct trigger because the
    // root cause is "textarea measured at wrong width during init".
    this._resizeObserver = new ResizeObserver(() => {
      const inlineH = parseInt(ta.style.height, 10);
      if (inlineH > 200) {
        fix();
      }
      // Disconnect after fixing — we only need this once at mount time
      if (!isNaN(inlineH) && inlineH <= 200) {
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
      }
    });
    this._resizeObserver.observe(ta);
  }

  ngAfterViewChecked(): void {
    performance.mark('chat-widget-cd-end');
    performance.measure('chat-widget-cd', 'chat-widget-cd-start', 'chat-widget-cd-end');
    const entries = performance.getEntriesByName('chat-widget-cd');
    const last = entries[entries.length - 1];
    if (last && last.duration > 5) {
      console.log(`[ChatWidget] CD took ${last.duration.toFixed(1)}ms (${this._messages.length} msgs)`);
    }
    performance.clearMeasures('chat-widget-cd');
    performance.clearMarks('chat-widget-cd-start');
    performance.clearMarks('chat-widget-cd-end');
  }

  @ViewChild('chat', { static: true }) chatScreen: ChatScreenComponent;
  @ViewChild('chatInputComponent') chatInputComponent!: ChatInputComponent;
  @ViewChild('userInputArea') userInputArea!: ElementRef<HTMLDivElement>;
  @ViewChild('combinedSelectorTrigger') combinedSelectorTrigger: MatMenuTrigger;

  @Input() uuid: string | undefined;
  @Input() title: string | undefined;
  @Input() workSessionId: string | undefined;
  @Input() workSessionAgentId: string | undefined;
  @Input() workSessionChannelId: string | undefined;
  @Input() workSessionWarmUp: string | undefined;
  @Input() workSessionStartCollapsed: boolean = false;
  @Input() set isFullScreen(value: boolean) {
    const newValue = value === true;
    if (newValue === this._isFullScreen) return; // No change
    this._isFullScreen = newValue;
    console.log('isFullScreen set to:', this._isFullScreen);
    this.cdr?.detectChanges();
  }

  get isFullScreen(): boolean {
    return this._isFullScreen === true;
  }

  private _isFullScreen: boolean = false;

  // Override isDocked from base class to handle session list auto-collapse
  @Input() override set isDocked(value: boolean) {
    const wasDocked = this._isDocked;
    this._isDocked = value;
    console.log('[ChatWidget] isDocked setter called:', { value, wasDocked, historyOpen: this._historyOpen, id: this.id });

    if (value === true && !wasDocked) {
      // Docking: save current state and collapse
      this._historyOpenBeforeDock = this._historyOpen;
      this._historyOpen = false;
      console.log('[ChatWidget] Docked - collapsed history panel, saved state:', this._historyOpenBeforeDock);
    } else if (value === false && wasDocked) {
      // Undocking: restore previous state
      if (this._historyOpenBeforeDock !== null) {
        this._historyOpen = this._historyOpenBeforeDock;
        this._historyOpenBeforeDock = null;
        console.log('[ChatWidget] Undocked - restored history panel to:', this._historyOpen);
      }
    }

    this.cdr?.detectChanges();
  }

  override get isDocked(): boolean {
    return this._isDocked;
  }

  @Output() onViewExperiments = new EventEmitter<{ projectName: string }>();
  @Output() onViewDefinition = new EventEmitter<{ projectName: string; definitionName: string }>();
  @Output() onViewBuild = new EventEmitter<{ projectName: string; runid: number }>();
  @Output() onViewAsset = new EventEmitter<{ projectName: string; assetName: string }>();
  @Output() onViewFeatureExplorer = new EventEmitter<{ projectName: string }>();
  @Output() onViewVault = new EventEmitter<{ projectName: string }>();
  @Output() onViewUpload = new EventEmitter();
  @Output() onEditMCPConfig = new EventEmitter<{ path: string }>();
  @Output() modelChange = new EventEmitter<string>();
  // Add output for opening widgets in full-screen mode
  @Output() onOpenWidget = new EventEmitter<string>();
  // Add output for opening agent settings widget
  @Output() onOpenAgentSettings = new EventEmitter<{ agentId: string | null }>();
  // Add output for opening backend detail widget
  @Output() onOpenBackendDetail = new EventEmitter<{ backendId: string }>();

  private _ticker: string | undefined;
  private _rawSessions: ChatService.ChatSession[] = [];
  public version: string | undefined;

  private _messages: ChatService.ChatMessage[] = [];
  public get messages(): ChatService.ChatMessage[] {
    return this._messages;
  }
  public set messages(value: ChatService.ChatMessage[]) {
    this._messages = value;
  }
  public disabled: boolean = false;
  private _historyOpen: boolean = true;
  private _historyOpenBeforeDock: boolean | null = null; // Stores state before docking
  public get historyOpen(): boolean {
    return this._historyOpen;
  }
  public set historyOpen(value: boolean) {
    this._historyOpen = value;
    if (this.teamName) {
      localStorage.setItem(`${this._storagePrefix}chat-history-open`, String(value));
    }
  }
  public loading = false;
  public availableModels: ChatService.AvailableModel[] = [];
  public model: string = 'deepseek-r1:32b';

  // Agent-related properties (lit-mux integration)
  public availableAgents: MuxAgentService.Agent[] = [];
  public currentAgentId: string = 'default';
  public agentsLoaded: boolean = false;
  public anchors: string[] = [];
  public sessions: [string, ChatService.ChatSession[]][] = [];
  public samples: Sample[] = [];
  private slashCommands: any[] = [];
  public slashCommandActive: boolean = false;
  private activeCommandId: string = null;
  public chatActive: boolean = false;
  private activeChatId: string = null;
  public safeMode: boolean = false;

  // Multi-select conversation properties
  public selectionMode: boolean = false;
  public selectedSessions: Set<string> = new Set();
  private lastSelectedIndex: number = -1;
  private _preSelectionSessionId: string | undefined;
  private longPressTimer: any = null;

  // Combined selector state (for mobile)
  public combinedSelectorView: 'sessions' | 'agents' = 'sessions';

  // Search properties
  public sessionSearchQuery: string = '';
  private _filteredSessions: [string, ChatService.ChatSession[]][] = [];
  public get filteredSessions(): [string, ChatService.ChatSession[]][] {
    const count = this._filteredSessions.reduce((sum, block) => sum + block[1].length, 0);
    // console.log(`[ChatWidget] filteredSessions accessed, ${count} sessions`);
    return this._filteredSessions;
  }
  public set filteredSessions(value: [string, ChatService.ChatSession[]][]) {
    this._filteredSessions = value;
  }
  public isSearching: boolean = false;
  private searchDebounceTimer: any = null;

  // Pagination properties for message loading
  public hasMoreMessages: boolean = false;
  public loadingMoreMessages: boolean = false;
  private currentMessagesOffset: number = 0;
  private readonly MESSAGES_PAGE_SIZE: number = 20;

  // Pinned messages / Knowledge graph pane
  public pinsPaneOpen: boolean = false;
  public pinnedMessages: MuxSessionService.PinnedMessage[] = [];
  public get pinnedCount(): number { return this.pinnedMessages.length; }
  private _pinsPanePercent: number = 30;
  public get pinsPanePercent(): number { return this._pinsPanePercent; }
  public set pinsPanePercent(value: number) {
    this._pinsPanePercent = Math.max(15, Math.min(50, value));
    this.saveAppPaneState();
  }
  // Knowledge graph in pins pane
  public pinsPaneView: 'graph' | 'messages' = 'graph';
  public graphSelectedNode: { id: string; type: string; label: string; count: number; messages: { ref: string; excerpt: string; message_id?: string }[] } | null = null;
  public graphNodeMessages: { ref: string; excerpt: string; content: string | null; loading: boolean }[] = [];
  public graphNodeMessagesLoading: boolean = false;
  public graphMessageFilter = '';
  private graphObserver: IntersectionObserver | null = null;

  // Team quick-switch properties
  public myTeams: { id: string; name: string; slug: string; members: string[] }[] = [];
  public favoriteTeams: Set<string> = new Set();
  private static readonly FAVORITE_TEAMS_KEY = 'lit-favorite-teams';

  /** Teams to show in quick-switch menu: favorites if any, otherwise all */
  public get visibleTeams(): { id: string; name: string; slug: string; members: string[] }[] {
    if (this.favoriteTeams.size === 0) {
      return this.myTeams;
    }
    return this.myTeams.filter(t => this.favoriteTeams.has(t.slug));
  }

  // Dynamic widgets (team-level apps like Odoo CRM)
  public dynamicWidgets: DynamicWidget[] = [];

  /** host.badge.set count for a launcher row ('' when none). */
  appBadge(widget: DynamicWidget): string {
    const n = this._dynamicWidgets.badgeFor(widget.name);
    return n > 0 ? DynamicWidgetsService.badgeLabel(n) : '';
  }

  // Pinned apps for toolbar
  private static readonly PINNED_APPS_KEY = 'lit-fullscreen-pinned-apps';
  private static readonly PINNED_DYNAMIC_WIDGETS_KEY = 'lit-fullscreen-pinned-dynamic-widgets';
  public pinnedWidgets: Set<string> = new Set();
  public pinnedDynamicWidgetIds: Set<string> = new Set();

  /** Get all pinned apps for toolbar display */
  public get pinnedApps(): { id: string; name: string; icon: string; type: 'widget' | 'dynamic'; widgetName?: string; dynamicWidget?: DynamicWidget }[] {
    const apps: { id: string; name: string; icon: string; type: 'widget' | 'dynamic'; widgetName?: string; dynamicWidget?: DynamicWidget }[] = [];

    // Add pinned built-in widgets
    for (const widgetName of this.pinnedWidgets) {
      apps.push({
        id: widgetName,
        name: widgetName.replace('Widget', ''),
        icon: this.WIDGET_ICONS[widgetName] || 'extension',
        type: 'widget',
        widgetName: widgetName
      });
    }

    // Add pinned dynamic widgets
    for (const widgetId of this.pinnedDynamicWidgetIds) {
      const widget = this.dynamicWidgets.find(w => w.id === widgetId);
      if (widget) {
        apps.push({
          id: widget.id,
          name: widget.title,
          icon: widget.icon || 'web',
          type: 'dynamic',
          dynamicWidget: widget
        });
      }
    }

    return apps;
  }

  public get session() : string {
    return `${this.teamName}:${this._identity.me.username}:${this.uuid}`;
  }

  public get modelName(): string {
    return this.availableModels.find((availableModel) => availableModel.model === this.model)?.name ?? 'Deepseek-R1-32B';
  }

  public get currentAgentName(): string {
    return this.availableAgents.find((agent) => agent.id === this.currentAgentId)?.name ?? 'Default Assistant';
  }

  public get currentBackendId(): string {
    const agent = this.availableAgents.find((agent) => agent.id === this.currentAgentId);
    return agent?.backend ?? 'claude-cli';
  }

  public get selfTurns() {
    return this.messages.filter(({ isSelf }) => isSelf);
  }

  public get defaultChat(): DefaultChat {
    return this._settings.defaultChat;
  }

  /** Per-widget localStorage key prefix (e.g. "myteam-ChatWidget2-") */
  private get _storagePrefix(): string {
    return `${this.teamName}-${this.id}-`;
  }

  public get lastChat(): string | undefined {
    // Try per-widget key first, fall back to legacy global key for migration
    const stored = localStorage.getItem(`${this._storagePrefix}last-chat`)
      ?? localStorage.getItem(`${this.teamName}-last-chat`)
      ?? undefined;
    if (this._rawSessions.find((value) => value.session.split(':').pop() === stored)) return stored;
    return undefined;
  }

  public get isDesktopEnvironment(): boolean {
    return this._settings.desktopEnvironment;
  }

  /** Check if this deployment is locked to full-screen mode (hide toggle buttons) */
  public get isFullScreenOnly(): boolean {
    return window['env']?.whiteLabel?.fullScreenOnly === true;
  }

  /** Check if this deployment forces a specific layout engine */
  public get hasForceLayout(): boolean {
    return !!window['env']?.whiteLabel?.forceLayout;
  }

  /** Check if skill packs should be shown (null = show all, [] = hide all) */
  public get showSkillPacks(): boolean {
    const configured = window['env']?.whiteLabel?.skillPacks;
    return configured === undefined || configured === null || configured.length > 0;
  }

  /** Get logo URL from white-label config, defaulting to LIT logo */
  public get logoUrl(): string | null {
    return window['env']?.whiteLabel?.logoUrl || 'assets/images/lit-logo.png';
  }

  public get username(): string {
    return this._identity.me.username;
  }

  public get llmName(): string {
    return `${this._ticker.toLocaleUpperCase()}.llm`;
  }

  // Expose router to the template
  public get router(): Router {
    return this._router;
  }

  // Variables for stream management
  private streamingMessageId: string | null = null;
  private streamingContent: string = '';
  private streamingMessageIndex: number = -1;
  private streamingTimeoutId: any = null; // To store the timeout ID

  // Telemetry WebSocket for real-time reaction updates
  private _telemetryWs: WebSocket | null = null;

  // Usage tracking
  public usageReport: MuxUsageService.UsageReport | null = null;
  public usageLoading: boolean = false;
  public usageRefreshing: boolean = false;

  // Backend auth status tracking
  public backendAuthStatus: 'healthy' | 'warning' | 'error' | 'unknown' = 'unknown';
  public backendAuthMessage: string = '';
  public backendTokenExpiry: string | null = null;

  // App pane state (full-screen mode only)
  public openApps: OpenApp[] = [];
  public activeAppId: string | null = null;
  private _appPaneHeight: number = 50; // percentage of split area
  private _appPaneHidden: boolean = false; // when true, app pane collapses (VS Code style)
  private _chatHidden: boolean = false; // when true, chat area collapses
  private _sidebarWidth: number = 280; // sidebar width in pixels

  // Getters/setters for persisted app pane state
  public get appPaneHeight(): number { return this._appPaneHeight; }
  public set appPaneHeight(value: number) {
    this._appPaneHeight = value;
    this.saveAppPaneState();
  }

  public get appPaneHidden(): boolean { return this._appPaneHidden; }
  public set appPaneHidden(value: boolean) {
    this._appPaneHidden = value;
    this.saveAppPaneState();
  }

  public get chatHidden(): boolean { return this._chatHidden; }
  public set chatHidden(value: boolean) {
    this._chatHidden = value;
    this.saveAppPaneState();
  }

  public get sidebarWidth(): number { return this._sidebarWidth; }
  public set sidebarWidth(value: number) {
    this._sidebarWidth = value;
    this.saveAppPaneState();
  }

  public get localMode(): boolean {
    return ConfigService.localMode;
  }

  public get changelogURL(): string {
    if (this.version !== undefined) {
      return `https://docs.lit.ai/reference/changelog/#${this.version}`;
    }
    return 'https://docs.lit.ai/reference/changelog/';
  }

  /** Returns the currently active app, or null if no app is open */
  public get activeApp(): OpenApp | null {
    return this.openApps.find(app => app.id === this.activeAppId) ?? null;
  }

  /** Returns true if the app pane should be visible */
  public get isAppPaneVisible(): boolean {
    return this.isFullScreen && this.openApps.length > 0 && !this.appPaneHidden;
  }

  constructor(
    private readonly _chat: ChatService,
    private _llm: LLMWorkflowService,
    private readonly _settings: SettingsService,
    public readonly _identity: IdentityService,
    private readonly _socket: SocketService,
    private readonly _widget: WidgetService,
    private readonly sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private readonly _version: VersionService,
    private readonly _router: Router,
    private activatedRoute: ActivatedRoute,
    private readonly _muxAgent: MuxAgentService,
    private readonly _muxSession: MuxSessionService,
    private readonly _muxStream: MuxStreamService,
    private readonly _muxUsage: MuxUsageService,
    private readonly _desktopState: DesktopStateService,
    private readonly _widgetContext: WidgetContextService,
    private readonly _snackBar: MatSnackBar,
    private readonly _http: HttpClient,
    private readonly _authWizard: ClaudeAuthWizardService,
    private readonly _teams: TeamsService,
    private readonly _userPrefs: UserPrefsService,
    private readonly _dynamicWidgets: DynamicWidgetsService
  ) {
    super();

    // Debug: expose instance globally
    (window as any).chatWidget = this;

    // Add a route monitor to detect if we're in full-screen chat mode
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // Check if we're in the chat page or grid page
        const isChatPage = event.url.includes('/chat');
        console.log('Route changed, detecting mode - isChatPage:', isChatPage);

        if (isChatPage) {
          this._isFullScreen = true;
        } else {
          this._isFullScreen = false;
        }
        this.cdr.detectChanges();
      }
    });
  }

  public ngOnInit() {
    if ((<any>window).chat) (<any>window).chat.push(this);
    else (<any>window).chat = [this];

    // Restore history panel state (but not if docked - docked widgets should stay collapsed)
    if (this.workSessionStartCollapsed) {
      this._historyOpen = false;
    } else if (!this._isDocked) {
      const savedHistoryOpen = localStorage.getItem(`${this._storagePrefix}chat-history-open`)
        ?? localStorage.getItem(`${this.teamName}-chat-history-open`);
      if (savedHistoryOpen !== null) {
        this._historyOpen = savedHistoryOpen === 'true';
      }
    }

    // Restore app pane state (full-screen mode only)
    this.restoreAppPaneState();

    // Get version for changelog link
    if (this.isFullScreen) {
      this._version.get().then(({ version }) => {
        this.version = version;
      });

      // Load teams for quick-switch menu
      this.loadFavoriteTeams();
      this._teams.load().subscribe((teams) => {
        this.myTeams = teams;
        this.cdr.detectChanges();
      });

      // Load dynamic widgets (team-level apps)
      this.loadDynamicWidgets();

      // Load pinned apps for toolbar
      this.loadPinnedApps();
      this.loadPinnedDynamicWidgets();
    }

    // DISABLED: Legacy slash commands - needs rewrite
    // this._llm.list(this.teamName).subscribe((response) => {
    //   this.slashCommands = <any[]>response;
    // });

    this._socket.joinRoom('chat');
    this._subs.add = this._socket.subscribeToRoomEvents('chat', (data: any) => {
      if (data.origin === 'experiment.change') {
        this.messages.push({ isSelf: false, content: data.msg });
      }
    });

    this._socket.joinRoom('llm');
    this._subs.add = this._socket.subscribeToRoomEvents('llm', (data: any) => {
      if (data.team == this.teamName && data.origin === 'udf' && data.session == this.session) {
        this.handleIncomingMessage(data);
      } else if (data.origin === 'chat') {
        // Handle title update event
        if (data.type === 'title-update') {
          console.log('Title update event received:', data);

          // Force a refresh of the sessions list
          this.updateSessions();

          // Update the current chat title if it matches our session
          if (data.session === this.session) {
            // Get the title from the server to ensure it's up to date
            this._chat.historyAsync(this.teamName, this._identity.me.username, this.uuid).then((response) => {
              console.log('Refreshed title from server:', response.title);
              // Update the title property to reflect in the UI
              this.title = response.title;
              this.cdr.detectChanges(); // Force UI update
            }).catch(err => {
              console.error('Error refreshing title:', err);
            });
          }
        } else {
          // Handle streaming chat events
          this.handleStreamEvent(data);
        }
      }
    });

    // Load agents from lit-mux (replaces model loading)
    this.loadAgents();

    // Subscribe to agent changes (when agents are created/updated/deleted)
    this._subs.add = this._muxAgent.agentsChanged$.subscribe(() => {
      this.refreshAgentsList();
    });

    // Subscribe to current agent changes (only from external sources like Setup wizard,
    // not from other ChatWidget instances — each widget manages its own agent independently)
    this._subs.add = this._muxAgent.currentAgent$.subscribe((agentId) => {
      // Only react if this widget hasn't chosen its own agent yet
      if (agentId && agentId !== this.currentAgentId && !localStorage.getItem(`${this._storagePrefix}agent-id`)) {
        this.currentAgentId = agentId;
        this.cdr.detectChanges();
      }
    });

    // Subscribe to usage updates
    this._subs.add = this._muxUsage.usage$.subscribe((report) => {
      this.usageReport = report;
      this.cdr.detectChanges();
    });
    this._subs.add = this._muxUsage.loading$.subscribe((loading) => {
      this.usageLoading = loading;
      this.cdr.detectChanges();
    });

    // Usage polling will be started after agents load (in loadAgents)

    // Check backend auth status when usage updates (piggyback on usage polling)
    // Note: Initial check happens in loadAgents() after agents are loaded,
    // because currentBackendId depends on the selected agent
    this._subs.add = this._muxUsage.usage$.subscribe(() => {
      if (this.agentsLoaded) {
        this.checkBackendAuthStatus();
      }
    });

    // Refresh backend status immediately when auth wizard completes
    this._subs.add = this._dynamicWidgets.badges$.subscribe(() => this.cdr.markForCheck());
    this._subs.add = this._authWizard.authStatus$.subscribe(status => {
      if (status === 'authenticated' && this.agentsLoaded) {
        console.log('[ChatWidget] Auth wizard completed, refreshing backend status');
        this.checkBackendAuthStatus();
      }
    });

    // In fullscreen (chat-page) mode, handle markdown viewer requests from the app-level menubar.
    // In grid modes, the grid-page handles this directly; here we open it as an app pane.
    if (this.isFullScreen) {
      this._subs.add = this._widget.viewMarkdownRequest$.subscribe(event => {
        this.openMarkdownViewer(event.path || '', event.title || 'New Document', event.startInEditMode ?? false);
      });
    }

    // Handle agent-requested app opens (via /open-app skill)
    this._subs.add = this._widget.openAppRequest$.subscribe(({ appId, inputs }) => {
      const dynWidget = this._dynamicWidgets.getWidget(appId);
      if (dynWidget) {
        this.openDynamicWidget(dynWidget);
      } else if ((appId + 'Widget') in WidgetService.REGISTRY) {
        this.openAppWithInputs(appId + 'Widget' as WidgetService.WidgetName, inputs || {});
      }
    });
  }

  /**
   * Refresh agents list (called when agents are created/updated/deleted)
   */
  private refreshAgentsList(): void {
    this.availableAgents = this._muxAgent.getAgents();
    this.cdr.detectChanges();
  }

  /**
   * Load agents from lit-mux
   */
  private async loadAgents(): Promise<void> {
    try {
      const agents = await this._muxAgent.loadAgents(this.teamName);
      this.availableAgents = agents;
      this.agentsLoaded = true;

      // Work session override: if opened from a channel work session, use that agent
      if (this.workSessionAgentId && agents.some(a => a.id === this.workSessionAgentId)) {
        this.currentAgentId = this.workSessionAgentId;
      } else {
        // Restore per-widget agent ID, falling back to global default
        const savedAgent = localStorage.getItem(`${this._storagePrefix}agent-id`);
        this.currentAgentId = savedAgent && agents.some(a => a.id === savedAgent)
          ? savedAgent
          : this._muxAgent.getCurrentAgentId();
      }

      console.log(`Loaded ${agents.length} agents from lit-mux, current: ${this.currentAgentId}`);
      this.cdr.detectChanges();

      // Start usage polling for the current agent's backend
      this._muxUsage.startPolling(this.currentBackendId, undefined, this.currentAgentId);

      // Refresh backend auth status now that we know the actual backend
      this.checkBackendAuthStatus();

      // Now load sessions for the restored agent
      this.updateSessions();
    } catch (error) {
      console.error('Failed to load agents:', error);
      // Set agentsLoaded to true anyway so user can still chat
      this.agentsLoaded = true;
      // Still try to load sessions with default agent
      this.updateSessions();
    }
  }

  /**
   * Handle agent change from selector
   */
  public $changeAgent(agentId: string): void {
    if (agentId === '__CREATE_NEW__') {
      this.openAppWithInputs('AgentSettingsWidget', { agentId: null });
      return;
    }

    if (agentId === '__EDIT_CURRENT__') {
      this.openAppWithInputs('AgentSettingsWidget', { agentId: this.currentAgentId });
      return;
    }

    this.currentAgentId = agentId;
    this._muxAgent.setCurrentAgent(agentId);
    localStorage.setItem(`${this._storagePrefix}agent-id`, agentId);

    // Clear conversation and load sessions for the new agent
    this.messages = [];
    this.anchors = [];
    this.uuid = undefined;
    this.updateSessions();

    // Restart usage polling for the new agent's backend
    this._muxUsage.startPolling(this.currentBackendId, undefined, this.currentAgentId);

    // Refresh backend auth status for the new backend
    this.checkBackendAuthStatus();

    console.log('Switched to agent:', agentId, 'backend:', this.currentBackendId);
  }

  /**
   * Open agent settings directly (for button click).
   * Always uses the internal app pane so it works in both full-screen and dockview modes.
   */
  public openAgentSettings(): void {
    const agents = this._muxAgent.getAgents();
    const hasAgents = agents && agents.length > 0;
    this.openAppWithInputs('AgentSettingsWidget', {
      agentId: hasAgents ? this.currentAgentId : null
    });
  }

  /**
   * Resolve a possibly-stale or name-slug agent identifier to a real agent id.
   * `currentAgentId` can drift to a display-name slug (e.g. 'claude' for the default
   * agent whose name is "Claude") through unvalidated paths; opening the settings
   * widget with that slug queries a non-existent agent, so getAgent/getAgentStimuli
   * return nothing and the tabs render empty. Match the loaded agent list by id first,
   * then by slugified name, then case-insensitive name; fall back to the raw value.
   */
  private _resolveAgentId(id: string | null): string | null {
    if (!id) return null;
    const agents = this._muxAgent.getAgents();
    if (agents.some(a => a.id === id)) return id;
    const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const bySlug = agents.find(a => a.name && slugify(a.name) === id);
    if (bySlug) return bySlug.id;
    const byName = agents.find(a => a.name && a.name.toLowerCase() === id.toLowerCase());
    return byName ? byName.id : id;
  }

  public ngOnDestroy(): void {
    this._socket.leaveRoom('chat');

    // Clear any streaming timeout
    if (this.streamingTimeoutId) {
      clearTimeout(this.streamingTimeoutId);
      this.streamingTimeoutId = null;
    }

    // Clear any long press timer
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    // Stop usage polling
    this._muxUsage.stopPolling();

    // Clean up textarea resize observer
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;

    // Close telemetry WebSocket
    this._disconnectTelemetryWs();

    super.ngOnDestroy();
  }

  /**
   * Called when the chat input textarea resizes (grows/shrinks).
   * Updates the bottom padding of the chat content to match the input overlay height,
   * so the user can scroll down to see content behind the overlapping input.
   */
  public onInputResized(): void {
    if (!this.userInputArea) return;
    const height = this.userInputArea.nativeElement.offsetHeight;
    this.chatScreen?.setContentPaddingBottom(height);
  }

  @debounce(100)
  public _scrollToBottom(): void {
    this.chatScreen.scrollToBottom();
  }

  /**
   * Force scroll to bottom and exit readback mode.
   * Use when the user sends a message.
   */
  @debounce(100)
  public _forceScrollToBottom(): void {
    this.chatScreen.forceScrollToBottom();
  }

  @debounce(100)
  private _focusPromptBox(): void {
    this.chatInputComponent?.focus();
  }

  private _handleSystemEvent(event: string): void {
    console.log(`🪝 System event: ${event}`);
  }

  private organizeChatSessions(sessions?: ChatService.ChatSession[]): [string, ChatService.ChatSession[]][] {
    sessions ??= this._rawSessions;
    const today = (() => {
      const today = new Date();
      today.setHours(0);
      today.setMinutes(0);
      today.setSeconds(0);
      today.setMilliseconds(0);
      return today.getTime();
    })();

    const getTimeframe = ({ mtime }: ChatService.ChatSession): string => {
      // Convert from seconds to milliseconds if needed
      const mtimeMs = mtime > 10000000000 ? mtime : mtime * 1000;

      if (mtimeMs >= today) return 'Today';
      else if (mtimeMs >= today - MS_PER_DAY) return 'Yesterday';
      else if (mtimeMs >= today - 7 * MS_PER_DAY) return 'Previous 7 Days';
      else if (mtimeMs >= today - 30 * MS_PER_DAY) return 'Previous 30 Days';
      return new Date(mtimeMs).getFullYear().toString();
    };

    const organizedSessions: typeof this.sessions = [];
    const seenTimeframes: Set<string> = new Set();
    sessions
      .sort((a, b) => b.mtime - a.mtime)
      .forEach((session) => {
        const timeframe = getTimeframe(session);
        if (seenTimeframes.has(timeframe)) {
          organizedSessions[organizedSessions.length - 1][1].push(session);
        } else {
          organizedSessions.push([timeframe, [session]]);
          seenTimeframes.add(timeframe);
        }
      });

    return organizedSessions;
  }

  private handleIncomingMessage(data: any) {
    if (!data || typeof data !== 'object') return;

    // Handle cached analysis retrieval
    if (data.s === 'ok' && data.components) {
      console.log('Processing cached analysis...');

      // Handle system prompt if it exists
      if (data.system_prompt) {
        let systemPromptName = 'System Prompt';
        this.messages.push({
          isSelf: true,
          title: systemPromptName,
          isAnchor: true,
          content: data.system_prompt
        });
        this.anchors.push(systemPromptName);
      }

      // Iterate through cached component analyses
      Object.entries(data.components).forEach(([promptName, component]: [string, any]) => {
        if (component.prompt && component.response) {
          // Store anchor for navigation
          this.anchors.push(promptName);

          // Emit user prompt as an anchor message
          this.messages.push({
            isSelf: true,
            title: promptName,
            content: component.prompt,
            timestamp: component.prompt_timestamp,
            isAnchor: true
          });

          // Emit the corresponding LLM response
          this.messages.push({
            isSelf: false,
            content: component.response,
            model: component.model,
            timestamp: component.response_timestamp,
            isAnchor: false
          });
        }
      });

      // Emit the final analysis if it exists
      if (data.final?.prompt) {
        this.messages.push({
          isSelf: true,
          content: data.final.prompt,
          timestamp: data.final.prompt_timestamp,
          isFinal: false
        });
      }
      if (data.final?.response) {
        this.messages.push({
          isSelf: false,
          content: data.final.response,
          model: data.final.model,
          timestamp: data.final.response_timestamp,
          isFinal: true
        });
      }

      this.cdr.detectChanges();
      this._scrollToBottom();
      return; // Stop processing further since we handled cached data
    }

    // Handle live processing hints (e.g., "Thinking about X...")
    if (data.content && data.content.startsWith('Thinking about')) {
      this.messages.push({
        isSelf: false,
        content: data.content,
        isHint: true
      });
    } else {
      if (data.isAnchor) {
        this.anchors.push(data.title); // Ensure anchors are tracked for live messages
      }

      // Remove any related "Thinking about" hint message
      const index = this.messages.findIndex((m) => {
        if (m.isHint && data.title) {
          // Check if content is a string and includes the title
          return typeof m.content === 'string' && m.content.includes(data.title);
        }
        return false;
      });
      if (index !== -1) {
        this.messages.splice(index, 1);
      }

      if(data.content && data.content.length > 0) {
        // Add the actual message
        this.messages.push({
          isSelf: data.isSelf,
          title: data.title ?? null,
          content: data.content,
          isAnchor: data.isAnchor ?? false,
          isFinal: data.isFinal ?? false,
          timestamp: data.timestamp
        });
      }
    }

    this.cdr.detectChanges();
    this._scrollToBottom();
  }

  public async getHistory(): Promise<void> {
    if (this.uuid === undefined) return;

    try {
      // Reset pagination state for new session
      this.currentMessagesOffset = 0;
      this.hasMoreMessages = false;

      // Load messages from lit-mux with pagination
      const result = await this._muxSession.loadSession(this.uuid, this.MESSAGES_PAGE_SIZE, 0, this.currentAgentId);

      // Create NEW array reference for OnPush change detection
      this.messages = result.messages.map((msg) => {
        // Extract LINKS: footer from assistant messages
        const rawContent = msg.role === 'user' ? this._stripSystemContext(msg.content) : msg.content;
        const { content: cleanContent, links } = ChatScreenComponent.extractLinks(rawContent);

        const message: ChatService.ChatMessage = {
          isSelf: msg.role === 'user',
          content: cleanContent,
          timestamp: msg.timestamp,
          messageId: msg.id,
          pinned: msg.pinned,
        };

        if (links.length) {
          message.links = links;
        }

        // Handle error messages - check metadata for is_error flag
        if (msg.metadata?.is_error) {
          message.isError = true;
        }

        // Handle turn path for easy reference to conversation turns
        if (msg.metadata?.turn_path) {
          message.turnPath = msg.metadata.turn_path;
        }

        // Handle widget messages - show screenshot on refresh
        if (msg.metadata?.widget_type) {
          message.title = msg.metadata.widget_title || msg.metadata.widget_type.replace('Widget', '');
          message.widgetScreenshot = msg.metadata.screenshot;
        }

        // Handle reactions
        if (msg.metadata?.reactions) {
          message.reactions = msg.metadata.reactions;
        }

        return message;
      });

      // Trigger CD since async operations may complete outside Angular zone
      this.cdr.detectChanges();

      // Update pagination state
      this.hasMoreMessages = result.has_more;
      this.currentMessagesOffset = result.messages.length;

      // Find the session to get its title
      const sessions = this._muxSession.getSessions();
      const session = sessions.find(s => s.id === this.uuid);
      if (session) {
        this.title = session.display_name || session.name || 'Work Session';
      }

      this.anchors = this.messages
        .slice(-40)
        .filter((x) => x.isAnchor)
        .map((x) => x.title);

      this._scrollToBottom();

      // Load pinned messages count (non-blocking)
      this.loadPinnedMessages();

      // Connect telemetry WS for real-time reaction updates
      if (this._settings.labsReactions) {
        this._connectTelemetryWs();
      }

      // Check for active stream and reconnect if needed
      this.checkAndReconnectStream();
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  }

  /**
   * Check if there's an active stream for this session and reconnect
   */
  private async checkAndReconnectStream(): Promise<void> {
    if (!this.uuid) return;

    // Create streaming message placeholder
    let streamingMessageIndex = -1;
    let fullResponse = '';

    const reconnected = await this._muxStream.checkAndReconnectStream(
      this.uuid,
      {
        onStart: (streamId: string, turnPath?: string) => {
          console.log('🔄 Reconnected to stream:', streamId, 'turnPath:', turnPath);
          this.chatActive = true;

          // Add streaming placeholder message
          streamingMessageIndex = this.messages.length;
          this.messages.push({
            isSelf: false,
            content: '',
            isStreaming: true,
            turnPath: turnPath
          });
          this.cdr.detectChanges();
        },
        onContent: (content: string) => {
          fullResponse = content;
          if (streamingMessageIndex >= 0 && this.messages[streamingMessageIndex]) {
            this.messages[streamingMessageIndex].content = content;
            this.messages[streamingMessageIndex].isStreaming = true;
            this.chatScreen.refresh();
            this.cdr.detectChanges();
            this._scrollToBottom();
          }
        },
        onError: (error: string) => {
          console.error('Stream reconnect error:', error);
          if (streamingMessageIndex >= 0 && this.messages[streamingMessageIndex]) {
            this.messages[streamingMessageIndex].content = `Error: ${error}`;
            this.messages[streamingMessageIndex].isError = true;
            this.messages[streamingMessageIndex].isStreaming = false;
          }
          this.chatActive = false;
          this.chatScreen.refresh();
          this.cdr.detectChanges();
        },
        onComplete: (content: string) => {
          console.log('✅ Stream reconnect complete');
          if (streamingMessageIndex >= 0 && this.messages[streamingMessageIndex]) {
            this.messages[streamingMessageIndex].content = content;
            this.messages[streamingMessageIndex].isStreaming = false;
          }
          this.chatActive = false;
          this.chatScreen.refresh();
          this.cdr.detectChanges();
          this._scrollToBottom();
          this._backfillMessageIds();
        },
        onSystemEvent: (event: string) => {
          this._handleSystemEvent(event);
        }
      }
    );

    if (reconnected === 'recently_completed') {
      // Stream completed between loadSession() and state check — the final assistant
      // message is now on disk but wasn't in our initial loadSession() response.
      // Re-fetch messages directly (don't call getHistory() to avoid re-triggering this check).
      console.log('⚡ Stream completed during page load, re-fetching messages');
      try {
        const result = await this._muxSession.loadSession(this.uuid, this.MESSAGES_PAGE_SIZE, 0, this.currentAgentId);
        this.messages = result.messages.map((msg) => {
          const rawContent = msg.role === 'user' ? this._stripSystemContext(msg.content) : msg.content;
          const { content: cleanContent, links } = ChatScreenComponent.extractLinks(rawContent);
          const message: ChatService.ChatMessage = {
            isSelf: msg.role === 'user',
            content: cleanContent,
            timestamp: msg.timestamp,
            messageId: msg.id,
            pinned: msg.pinned,
          };
          if (links.length) message.links = links;
          if (msg.metadata?.is_error) message.isError = true;
          if (msg.metadata?.turn_path) message.turnPath = msg.metadata.turn_path;
          if (msg.metadata?.widget_type) {
            message.title = msg.metadata.widget_title || msg.metadata.widget_type.replace('Widget', '');
            message.widgetScreenshot = msg.metadata.screenshot;
          }
          if (msg.metadata?.reactions) message.reactions = msg.metadata.reactions;
          return message;
        });
        this.cdr.detectChanges();
        this._scrollToBottom();
      } catch (e) {
        console.error('Failed to re-fetch messages after stream completion:', e);
      }
    } else if (reconnected) {
      console.log('🔄 Successfully reconnected to active stream');
    }
  }

  /**
   * Wait for the server to come back after a connection loss, then reload
   * the session to recover the saved partial response.
   */
  private async _waitForServerAndReload(streamingMessageIndex: number): Promise<void> {
    const healthUrl = `${ConfigService.muxUrl}/health`;
    const maxAttempts = 30; // ~30 seconds
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 1000));
      try {
        const resp = await fetch(healthUrl, { signal: AbortSignal.timeout(2000) });
        if (resp.ok) {
          console.log('Server is back — reloading session');
          // Remove the streaming placeholder
          if (streamingMessageIndex >= 0 && this.messages[streamingMessageIndex]) {
            this.messages.splice(streamingMessageIndex, 1);
          }
          this.loading = false;
          this.chatActive = false;
          // Reload full session from disk (includes the saved partial)
          await this.getHistory();
          return;
        }
      } catch {}
    }
    // Server didn't come back — show error
    console.error('Server did not recover after 30s');
    if (streamingMessageIndex >= 0 && this.messages[streamingMessageIndex]) {
      this.messages[streamingMessageIndex].content = 'Connection lost. Please refresh to recover.';
      this.messages[streamingMessageIndex].isError = true;
      this.messages[streamingMessageIndex].isStreaming = false;
    }
    this.loading = false;
    this.chatActive = false;
    this.chatScreen.refresh();
    this.cdr.detectChanges();
  }

  public async loadMoreMessages(): Promise<void> {
    if (!this.uuid || this.loadingMoreMessages || !this.hasMoreMessages) return;

    try {
      this.loadingMoreMessages = true;

      const result = await this._muxSession.loadSession(
        this.uuid,
        this.MESSAGES_PAGE_SIZE,
        this.currentMessagesOffset,
        this.currentAgentId
      );

      // Prepend older messages to the beginning
      const olderMessages = result.messages.map((msg) => {
        const rawContent = msg.role === 'user' ? this._stripSystemContext(msg.content) : msg.content;
        const { content: cleanContent, links } = ChatScreenComponent.extractLinks(rawContent);

        const message: ChatService.ChatMessage = {
          isSelf: msg.role === 'user',
          content: cleanContent,
          timestamp: msg.timestamp,
          messageId: msg.id,
          pinned: msg.pinned,
        };

        if (links.length) {
          message.links = links;
        }

        // Handle error messages - check metadata for is_error flag
        if (msg.metadata?.is_error) {
          message.isError = true;
        }

        // Handle turn path for easy reference to conversation turns
        if (msg.metadata?.turn_path) {
          message.turnPath = msg.metadata.turn_path;
        }

        // Handle widget messages - show screenshot on refresh
        if (msg.metadata?.widget_type) {
          message.title = msg.metadata.widget_title || msg.metadata.widget_type.replace('Widget', '');
          message.widgetScreenshot = msg.metadata.screenshot;
        }

        // Handle reactions
        if (msg.metadata?.reactions) {
          message.reactions = msg.metadata.reactions;
        }

        return message;
      });

      this.messages.unshift(...olderMessages);

      // Update pagination state
      this.hasMoreMessages = result.has_more;
      this.currentMessagesOffset += result.messages.length;
    } catch (error) {
      console.error('Failed to load more messages:', error);
    } finally {
      this.loadingMoreMessages = false;
    }
  }

  // Multi-select conversation helper methods
  public get hasSelectedSessions(): boolean {
    return this.selectedSessions.size > 0;
  }

  public get selectedSessionsCount(): number {
    return this.selectedSessions.size;
  }

  public isSessionSelected(session: ChatService.ChatSession): boolean {
    return this.selectedSessions.has(this.getSessionId(session));
  }

  public isActiveSession(session: ChatService.ChatSession): boolean {
    return this.uuid === this.getSessionId(session);
  }

  private getSessionId(session: ChatService.ChatSession): string {
    return session.session.split(':').pop() || '';
  }

  // Public wrapper for template use
  public getSessionIdPublic(session: ChatService.ChatSession): string {
    return this.getSessionId(session);
  }

  // Get the short session ID (first 8 chars) for display
  public get shortSessionId(): string {
    if (!this.uuid) return '';
    return this.uuid.substring(0, 8);
  }

  // Copy the turns folder path to clipboard
  public async $copyTurnsPath(): Promise<void> {
    if (!this.uuid) return;

    // Try to get the turn path from the most recent message that has one
    let turnPath: string | undefined;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].turnPath) {
        turnPath = this.messages[i].turnPath;
        break;
      }
    }

    // Get the directory (parent of the turn file)
    const path = turnPath
      ? turnPath.substring(0, turnPath.lastIndexOf('/'))
      : null;

    if (!path) {
      this._snackBar.open('Turn path not available', '', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(path);
      this._snackBar.open('Path copied to clipboard', '', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } catch (err) {
      console.error('Failed to copy path:', err);
    }
  }

  // Usage indicator helpers
  public get primaryUsageQuota(): MuxUsageService.UsageQuota | null {
    if (!this.usageReport?.available || !this.usageReport.quotas.length) {
      return null;
    }
    // Return the "Session" quota as primary, or the first one
    return this.usageReport.quotas.find(q => q.name === 'Session') || this.usageReport.quotas[0];
  }

  public formatUsagePercentage(used: number): string {
    return MuxUsageService.formatPercentage(used);
  }

  public formatUsageResetTime(resetsAt: string): string {
    return MuxUsageService.formatResetTime(resetsAt);
  }

  public getUsageClass(used: number): string {
    return MuxUsageService.getUsageClass(used, this.usageReport);
  }

  public async $refreshUsage(): Promise<void> {
    if (this.usageRefreshing) return;

    this.usageRefreshing = true;
    this.cdr.detectChanges();

    try {
      await this._muxUsage.fetchUsage(this.currentBackendId, this.currentAgentId);
      this._snackBar.open('Usage refreshed', '', {
        duration: 1500,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } catch (error) {
      console.error('Failed to refresh usage:', error);
    } finally {
      this.usageRefreshing = false;
      this.cdr.detectChanges();
    }
  }

  public getUsageTooltip(): string {
    if (!this.usageReport?.available) {
      return 'Usage data unavailable';
    }

    const lines: string[] = [];
    for (const quota of this.usageReport.quotas) {
      const pct = MuxUsageService.formatPercentage(quota.used);
      const reset = quota.resets_at ? ` (resets in ${MuxUsageService.formatResetTime(quota.resets_at)})` : '';
      lines.push(`${quota.name}: ${pct} used${reset}`);
    }
    return lines.join('\n');
  }

  // Backend auth status helpers
  public async checkBackendAuthStatus(): Promise<void> {
    const backendId = this.currentBackendId;
    try {
      const response = await this._http.get<any>(`${ConfigService.muxUrl}/backends/${backendId}/status`).toPromise();

      // Handle backends with OAuth (claude-cli)
      if (response.auth_status === 'authenticated') {
        if (response.warning) {
          this.backendAuthStatus = 'warning';
          this.backendAuthMessage = response.warning;
        } else {
          this.backendAuthStatus = 'healthy';
          this.backendAuthMessage = 'Authenticated';
        }
        if (response.token_details?.expires_in) {
          this.backendTokenExpiry = response.token_details.expires_in;
        }
      } else if (response.auth_status === 'token_expired') {
        this.backendAuthStatus = 'error';
        this.backendAuthMessage = response.error || 'Token expired';
        this.backendTokenExpiry = null;
      } else if (response.auth_status === 'not_authenticated' || response.auth_status === 'not_installed' || response.auth_status === 'not_configured') {
        this.backendAuthStatus = 'error';
        this.backendAuthMessage = response.error || 'Not configured';
        this.backendTokenExpiry = null;
      // Handle backends with simple API keys (gemini, etc.) - no auth_status field
      } else if (response.configured && response.healthy) {
        this.backendAuthStatus = 'healthy';
        this.backendAuthMessage = 'Configured';
        this.backendTokenExpiry = null;
      } else if (response.configured && !response.healthy) {
        this.backendAuthStatus = 'error';
        this.backendAuthMessage = response.error || 'Health check failed';
        this.backendTokenExpiry = null;
      } else if (!response.configured) {
        this.backendAuthStatus = 'error';
        this.backendAuthMessage = response.error || 'Not configured';
        this.backendTokenExpiry = null;
      } else {
        this.backendAuthStatus = 'unknown';
        this.backendAuthMessage = '';
        this.backendTokenExpiry = null;
      }
    } catch (error) {
      console.error(`Failed to check backend auth status for ${backendId}:`, error);
      this.backendAuthStatus = 'unknown';
      this.backendAuthMessage = '';
      this.backendTokenExpiry = null;
    }
    this.cdr.detectChanges();
  }

  public getAuthStatusIcon(): string {
    switch (this.backendAuthStatus) {
      case 'healthy': return 'check_circle';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'help';
    }
  }

  public getAuthStatusTooltip(): string {
    const backendId = this.currentBackendId;
    if (this.backendAuthStatus === 'healthy' && this.backendTokenExpiry) {
      return `${backendId}: Authenticated\nExpires: ${this.backendTokenExpiry}`;
    }
    if (this.backendAuthStatus === 'healthy') {
      return `${backendId}: ${this.backendAuthMessage}`;
    }
    if (this.backendAuthStatus === 'warning') {
      return `${backendId}: ${this.backendAuthMessage}\nExpires: ${this.backendTokenExpiry || 'unknown'}`;
    }
    if (this.backendAuthStatus === 'error') {
      return `${backendId}: ${this.backendAuthMessage}\nClick to configure`;
    }
    return `${backendId}: Status unknown\nClick to configure`;
  }

  public openBackendDetailFromHeader(): void {
    // In full-screen mode, render backend detail inline in chat
    if (this.isFullScreen) {
      this.openWidget('BackendDetailWidget');
      return;
    }
    this.onOpenBackendDetail.emit({ backendId: this.currentBackendId });
  }

  private getFlatSessionsList(): ChatService.ChatSession[] {
    const flatList: ChatService.ChatSession[] = [];
    this.sessions.forEach(([timeframe, sessions]) => {
      flatList.push(...sessions);
    });
    return flatList;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.selectionMode) {
      this.exitSelectionMode();
    }
  }

  public $toggleSelectionMode(): void {
    this.selectionMode = !this.selectionMode;
    if (!this.selectionMode) {
      this.selectedSessions.clear();
      this.lastSelectedIndex = -1;
    }
  }

  public enterSelectionMode(): void {
    if (!this.selectionMode) {
      this._preSelectionSessionId = this.uuid;
      this.selectionMode = true;
      this.clearTextSelection();
    }
  }

  public exitSelectionMode(): void {
    this.selectionMode = false;
    this.selectedSessions.clear();
    this.lastSelectedIndex = -1;
    this.clearTextSelection();

    // Restore the session that was active before entering selection mode
    const targetId = this._preSelectionSessionId;
    this._preSelectionSessionId = undefined;

    const targetSession = targetId ? this.findSessionByUuid(targetId) : null;
    if (targetSession) {
      this.$clickChat(targetSession);
    } else {
      // Fall back to first session
      const flatSessions = this.getFlatSessionsList();
      if (flatSessions.length > 0) {
        this.$clickChat(flatSessions[0]);
      }
    }
  }

  // Search methods
  public $onSearchChange(query: string): void {
    this.sessionSearchQuery = query;

    // Clear any pending debounce
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // If empty, show all sessions immediately
    if (!query.trim()) {
      this.filteredSessions = this.sessions;
      this.isSearching = false;
      return;
    }

    // Debounce API call for 300ms
    this.isSearching = true;
    this.cdr.detectChanges();
    this.searchDebounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  public async $clearSearch(): Promise<void> {
    this.sessionSearchQuery = '';
    this.isSearching = false;
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Reload all sessions without search filter
    this.isClearingSearch = true;
    this.cdr.detectChanges();

    try {
      const muxSessions = await this._muxSession.loadSessions(this.currentAgentId);
      this._rawSessions = muxSessions.map(s => ({
        session: `${this.teamName}:${this.username}:${s.id}`,
        title: s.display_name || s.name || 'New Session',
        mtime: new Date(s.updated_at).getTime()
      })) as ChatService.ChatSession[];

      this.sessions = this.organizeChatSessions();
      this.filteredSessions = this.sessions;
    } catch (error) {
      console.error('Failed to reload sessions:', error);
    } finally {
      this.isClearingSearch = false;
      this.cdr.detectChanges();
    }
  }

  public $onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.$clearSearch();
      event.preventDefault();
    }
  }

  private async performSearch(query: string): Promise<void> {
    try {
      // Call lit-mux API with search query (searches message content)
      const muxSessions = await this._muxSession.loadSessions(this.currentAgentId, query);

      // Convert to UI format
      this._rawSessions = muxSessions.map(s => ({
        session: `${this.teamName}:${this.username}:${s.id}`,
        title: s.display_name || s.name || 'New Session',
        mtime: new Date(s.updated_at).getTime()
      })) as ChatService.ChatSession[];

      this.sessions = this.organizeChatSessions();
      this.filteredSessions = this.sessions;
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      this.isSearching = false;
      this.cdr.detectChanges();
    }
  }

  private updateFilteredSessions(): void {
    // When not searching, just sync filteredSessions with sessions
    if (!this.sessionSearchQuery.trim()) {
      this.filteredSessions = this.sessions;
    }
  }

  // Pinned messages methods
  public togglePinsPane(): void {
    this.pinsPaneOpen = !this.pinsPaneOpen;
    if (this.pinsPaneOpen) {
      this.pinsPaneView = 'graph';
      this.graphSelectedNode = null;
      this.graphNodeMessages = [];
    }
    this.cdr.detectChanges();
  }

  public async loadPinnedMessages(): Promise<void> {
    if (!this.uuid) return;
    this.pinnedMessages = await this._muxSession.getPinnedMessages(this.uuid);
    this.cdr.detectChanges();
  }

  public async unpinFromPane(pin: MuxSessionService.PinnedMessage): Promise<void> {
    if (!this.uuid) return;
    await this._muxSession.unpinMessage(this.uuid, pin.id);
    this.pinnedMessages = this.pinnedMessages.filter(p => p.id !== pin.id);
    // Also update the message in the chat view
    const msg = this.messages.find(m => m.messageId === pin.id);
    if (msg) msg.pinned = false;
    this.cdr.detectChanges();
  }

  public seekToMessage(pin: MuxSessionService.PinnedMessage): void {
    const el = document.querySelector(`[data-message-id="${pin.id}"]`) as HTMLElement;
    if (el) {
      // Find the chat scroll container (.content) and scroll within it only,
      // avoiding scrollIntoView which bubbles up and displaces the header bar.
      const scrollContainer = el.closest('.content') as HTMLElement;
      if (scrollContainer) {
        const elRect = el.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const offsetInContainer = elRect.top - containerRect.top + scrollContainer.scrollTop;
        const centeredScroll = offsetInContainer - (containerRect.height / 2) + (elRect.height / 2);
        scrollContainer.scrollTo({ top: centeredScroll, behavior: 'smooth' });
      }
      el.classList.add('highlight-flash');
      setTimeout(() => el.classList.remove('highlight-flash'), 2000);
    } else {
      this._snackBar.open('Message is outside the loaded history — load earlier messages to reach it', '', {
        duration: 3000,
      });
    }
  }

  public isLongContent(content: string): boolean {
    return content && content.length > 400;
  }

  public hasToolCalls(content: string): boolean {
    return this.chatScreen?.hasToolCalls(content) ?? false;
  }

  public onPinsChanged(): void {
    this.loadPinnedMessages();
  }

  public onGraphNodeSelected(node: { id: string; type: string; label: string; count: number; messages: { ref: string; excerpt: string; message_id?: string }[] }): void {
    this.graphSelectedNode = node;
    this.pinsPaneView = 'messages';
    this.graphMessageFilter = '';
    this.graphNodeMessages = node.messages.map(m => ({
      ref: m.ref,
      excerpt: m.excerpt || '',
      content: null,
      loading: false,
    }));
    this.graphNodeMessagesLoading = false;
    this.cdr.detectChanges();
    this.setupGraphContentObserver();
  }

  get filteredGraphNodeMessages(): typeof this.graphNodeMessages {
    if (!this.graphMessageFilter.trim()) return this.graphNodeMessages;
    const q = this.graphMessageFilter.toLowerCase();
    return this.graphNodeMessages.filter(m =>
      m.excerpt.toLowerCase().includes(q) || m.ref.toLowerCase().includes(q) ||
      (m.content && m.content.toLowerCase().includes(q))
    );
  }

  public graphPaneBack(): void {
    this.pinsPaneView = 'graph';
    this.graphSelectedNode = null;
    this.graphNodeMessages = [];
    this.graphMessageFilter = '';
    this.teardownGraphContentObserver();
    this.cdr.detectChanges();
  }

  private setupGraphContentObserver(): void {
    this.teardownGraphContentObserver();
    setTimeout(() => {
      const container = document.querySelector('.pins-list') as HTMLElement;
      if (!container) return;
      this.graphObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const idx = parseInt(el.getAttribute('data-graph-msg-idx') || '-1', 10);
          if (idx < 0 || idx >= this.graphNodeMessages.length) continue;
          const msg = this.graphNodeMessages[idx];
          if (msg.content !== null || msg.loading) continue;
          msg.loading = true;
          this.cdr.detectChanges();
          const url = `${ConfigService.muxUrl}/knowledge-graph/message?ref=${encodeURIComponent(msg.ref)}`;
          this._http.get<{ content: string | null }>(url).subscribe({
            next: (res) => {
              msg.content = res.content || null;
              msg.loading = false;
              this.cdr.detectChanges();
            },
            error: () => {
              msg.content = null;
              msg.loading = false;
              this.cdr.detectChanges();
            }
          });
          this.graphObserver?.unobserve(el);
        }
      }, { root: container, rootMargin: '200px' });
      container.querySelectorAll('[data-graph-msg-idx]').forEach(el => this.graphObserver!.observe(el));
    }, 50);
  }

  private teardownGraphContentObserver(): void {
    if (this.graphObserver) {
      this.graphObserver.disconnect();
      this.graphObserver = null;
    }
  }

  onGraphFilterChange(): void {
    this.cdr.detectChanges();
    this.setupGraphContentObserver();
  }

  public formatGraphMsgRef(ref: string): string {
    const parts = ref.split('/');
    if (parts.length >= 3) {
      const channel = parts[0];
      const date = parts[1];
      const file = parts[2].replace('.md', '').replace(/_/g, ' ');
      return `#${channel} · ${date} · ${file}`;
    }
    return ref;
  }

  private clearTextSelection(): void {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
    }
  }

  public $onConversationClick(session: ChatService.ChatSession, event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey) {
      // Ctrl+click entry - selection already prevented in mousedown
      this.enterSelectionMode();

      // Select the currently active session if it exists
      if (this.uuid) {
        const currentSession = this.findSessionByUuid(this.uuid);
        if (currentSession) {
          this.selectSession(currentSession);
        }
      }

      // Toggle the clicked session
      this.toggleSessionSelection(session);
    } else if (event.shiftKey) {
      // Shift+click entry - selection already prevented in mousedown
      this.enterSelectionMode();

      // If we have a current session, select range from current to clicked
      if (this.uuid) {
        const currentSession = this.findSessionByUuid(this.uuid);
        if (currentSession) {
          this.selectRangeFromTo(currentSession, session);
        } else {
          // Fallback to just selecting the clicked session
          this.selectSession(session);
        }
      } else {
        // No current session, just select the clicked one
        this.selectSession(session);
      }
    } else if (!this.selectionMode) {
      // Normal single click - load conversation
      this.$clickChat(session);
    } else {
      // Regular click in selection mode - toggle selection
      this.toggleSessionSelection(session);
    }
  }

  public $onMouseDown(session: ChatService.ChatSession, event: MouseEvent): void {
    // Prevent text selection for any special key combinations
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();
      this.clearTextSelection();
      return; // Don't start long press timer for special clicks
    }

    // Handle long press for normal clicks only (left button, not in selection mode, no special keys)
    if (event.button === 0 && !this.selectionMode) {
      this.longPressTimer = setTimeout(() => {
        this.enterSelectionMode();
        this.selectSession(session);
        this.longPressTimer = null;
      }, 500);
    }
  }

  public $onMouseUp(event: MouseEvent): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  /**
   * Handle session selection from the mobile dropdown
   */
  public $selectSessionFromDropdown(sessionId: string): void {
    if (!sessionId) return;

    const flatSessions = this.getFlatSessionsList();
    const session = flatSessions.find(s => this.getSessionId(s) === sessionId);
    if (session) {
      this.$clickChat(session);
    }
  }

  /**
   * Switch combined selector to show agents list
   */
  public $showAgentSelector(): void {
    this.combinedSelectorView = 'agents';
  }

  /**
   * Switch combined selector back to sessions list
   */
  public $showSessionSelector(): void {
    this.combinedSelectorView = 'sessions';
  }

  /**
   * Handle agent selection from combined mobile selector
   * Menu stays open so user can select a session in the new agent
   */
  public $selectAgentFromCombinedSelector(agentId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (agentId === '__CREATE_NEW__') {
      this.openAppWithInputs('AgentSettingsWidget', { agentId: null });
      return;
    }

    this.$changeAgent(agentId);
    // Switch to sessions view for the new agent
    this.combinedSelectorView = 'sessions';

    // Reopen menu after a brief delay to show sessions for the new agent
    setTimeout(() => {
      if (this.combinedSelectorTrigger) {
        this.combinedSelectorTrigger.openMenu();
      }
    }, 50);
  }

  public $onMouseLeave(event: MouseEvent): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  public $onMainChatAreaClick(event: MouseEvent): void {
    if (this.selectionMode) {
      // Only exit if clicking on the main chat area itself, not on child elements
      const target = event.target as HTMLElement;
      if (target.classList.contains('main-chat-container') ||
          target.closest('.main-chat-container') === event.currentTarget) {
        this.exitSelectionMode();
      }
    }
  }

  private toggleSessionSelection(session: ChatService.ChatSession): void {
    const sessionId = this.getSessionId(session);
    if (this.selectedSessions.has(sessionId)) {
      this.selectedSessions.delete(sessionId);
    } else {
      this.selectedSessions.add(sessionId);
    }

    // Update last selected index for potential shift-click operations
    const flatSessions = this.getFlatSessionsList();
    this.lastSelectedIndex = flatSessions.findIndex(s => this.getSessionId(s) === sessionId);
  }

  public $toggleSessionSelection(session: ChatService.ChatSession): void {
    this.toggleSessionSelection(session);
  }

  private selectSession(session: ChatService.ChatSession): void {
    const sessionId = this.getSessionId(session);
    this.selectedSessions.add(sessionId);

    const flatSessions = this.getFlatSessionsList();
    this.lastSelectedIndex = flatSessions.findIndex(s => this.getSessionId(s) === sessionId);
  }

  private selectRange(endSession: ChatService.ChatSession): void {
    const flatSessions = this.getFlatSessionsList();
    const endIndex = flatSessions.findIndex(s => this.getSessionId(s) === this.getSessionId(endSession));

    if (this.lastSelectedIndex >= 0) {
      const start = Math.min(this.lastSelectedIndex, endIndex);
      const end = Math.max(this.lastSelectedIndex, endIndex);

      for (let i = start; i <= end; i++) {
        this.selectedSessions.add(this.getSessionId(flatSessions[i]));
      }
    } else {
      this.selectedSessions.add(this.getSessionId(endSession));
    }

    this.lastSelectedIndex = endIndex;
  }

  private findSessionByUuid(uuid: string): ChatService.ChatSession | null {
    const flatSessions = this.getFlatSessionsList();
    return flatSessions.find(session => this.getSessionId(session) === uuid) || null;
  }

  private selectRangeFromTo(startSession: ChatService.ChatSession, endSession: ChatService.ChatSession): void {
    const flatSessions = this.getFlatSessionsList();
    const startIndex = flatSessions.findIndex(s => this.getSessionId(s) === this.getSessionId(startSession));
    const endIndex = flatSessions.findIndex(s => this.getSessionId(s) === this.getSessionId(endSession));

    if (startIndex >= 0 && endIndex >= 0) {
      const start = Math.min(startIndex, endIndex);
      const end = Math.max(startIndex, endIndex);

      for (let i = start; i <= end; i++) {
        this.selectedSessions.add(this.getSessionId(flatSessions[i]));
      }

      this.lastSelectedIndex = endIndex;
    } else {
      // Fallback if we can't find both sessions
      this.selectSession(endSession);
    }
  }

  public async $bulkDeleteSessions(): Promise<void> {
    if (this.selectedSessions.size === 0) return;

    const confirmMessage = `Delete ${this.selectedSessions.size} conversation${this.selectedSessions.size > 1 ? 's' : ''}?`;
    if (!await this.dialogs.confirm(confirmMessage)) return;

    const currentUuid = this.uuid;
    const sessionsToDelete = Array.from(this.selectedSessions);

    try {
      // Delete via lit-mux session service
      await this._muxSession.deleteSessions(sessionsToDelete);

      // Remove deleted sessions from our local state
      this._rawSessions = this._rawSessions.filter(session =>
        !this.selectedSessions.has(this.getSessionId(session))
      );
      this.sessions = this.organizeChatSessions();
      this.updateFilteredSessions();

      // Clear selection and exit selection mode
      this.selectedSessions.clear();
      this.exitSelectionMode();

      // If current chat was deleted, switch to another or create new if none left
      if (sessionsToDelete.includes(currentUuid)) {
        if (this._rawSessions.length === 0) {
          this.$clickNewChat();
        } else {
          this.$clickChat(this._rawSessions[0]);
        }
      }
    } catch (err) {
      console.error('Error deleting sessions:', err);
      this.dialogs.alert('Error deleting some conversations. Please try again.');
    }
  }

  public $selectAllSessions(): void {
    const flatSessions = this.getFlatSessionsList();
    flatSessions.forEach(session => {
      this.selectedSessions.add(this.getSessionId(session));
    });
  }

  public get hasActiveSession(): boolean {
    return !!this.uuid && this.findSessionByUuid(this.uuid) !== null;
  }

  public $renameActiveSession(): void {
    if (!this.hasActiveSession) return;

    const activeSession = this.findSessionByUuid(this.uuid);
    if (activeSession) {
      this.$clickRenameSession(activeSession);
    }
  }

  public isArchiving = false;

  public async $archiveWorkSession(): Promise<void> {
    if (!this.workSessionId || !this.workSessionChannelId || this.isArchiving) return;
    this.isArchiving = true;
    this.cdr.detectChanges();
    try {
      const ok = await this._muxSession.archiveWorkSession(this.workSessionChannelId, this.workSessionId);
      if (ok) {
        // Clear workSessionId so the button disappears and widget behaves as a normal session
        this.workSessionId = undefined;
        this.workSessionChannelId = undefined;
      }
    } finally {
      this.isArchiving = false;
      this.cdr.detectChanges();
    }
  }

  public async $deleteActiveSession(): Promise<void> {
    if (!this.hasActiveSession) return;

    const confirmMessage = `Delete this conversation?`;
    if (!await this.dialogs.confirm(confirmMessage)) return;

    const activeSession = this.findSessionByUuid(this.uuid);
    if (activeSession) {
      this.$clickDeleteSession(activeSession);
    }
  }

  // Compact session state
  public isCompacting: boolean = false;

  // Loading states for session operations
  public isRenaming: boolean = false;
  public isDeleting: boolean = false;
  public isClearingSearch: boolean = false;

  public async $compactActiveSession(): Promise<void> {
    if (!this.hasActiveSession || this.isCompacting) return;

    const confirmMessage = `Compact this conversation? This will summarize the conversation to reduce context size.`;
    if (!await this.dialogs.confirm(confirmMessage)) return;

    try {
      this.isCompacting = true;
      this.cdr.detectChanges();

      const result = await this._muxSession.compactSession(this.uuid);

      // Show success message
      const preTokens = result.pre_tokens ? ` (was ${result.pre_tokens.toLocaleString()} tokens)` : '';
      this.messages.push({
        isSelf: false,
        content: `Session compacted successfully${preTokens}`,
        lineBreak: true
      });

      // Reload the session to show updated content
      await this.getHistory();
      this._scrollToBottom();
    } catch (error: any) {
      console.error('Failed to compact session:', error);
      this.messages.push({
        isSelf: false,
        content: `Failed to compact session: ${error.message || 'Unknown error'}`,
        isError: true
      });
    } finally {
      this.isCompacting = false;
      this.cdr.detectChanges();
    }
  }

  public $clearSelection(): void {
    this.selectedSessions.clear();
    this.lastSelectedIndex = -1;
  }

  /**
   * Handle submit event from the chat input component
   */
  public $onInputSubmit(event: ChatInputSubmitEvent): void {
    this.$submit(event.content);
  }

  public async $submit(prompt?: string): Promise<void> {
    // Content now comes from ChatInputComponent which handles image uploads
    if (!prompt) return;
    if (this.loading || !this.agentsLoaded) return;

    // Auto-create a session if none exists
    if (this.uuid === undefined) {
      try {
        const agent = this.availableAgents.find(a => a.id === this.currentAgentId);
        const backends = agent ? [agent.backend] : ['claude-cli'];
        const session = await this._muxSession.createSession(this.currentAgentId, backends);
        if (session) {
          this.uuid = session.id;
          this.title = session.display_name || 'New Session';
          localStorage.setItem(`${this._storagePrefix}last-chat`, this.uuid);
          this.updateSessions();
        } else {
          console.error('Failed to create session: no session returned');
          this.messages.push({ isSelf: false, content: 'Failed to create chat session. Please try again.', isError: true });
          return;
        }
      } catch (error: any) {
        console.error('Failed to auto-create session:', error);
        const errorMsg = error?.error?.detail || error?.message || 'Unknown error';
        this.messages.push({ isSelf: false, content: `Failed to create chat session: ${errorMsg}`, isError: true });
        return;
      }
    }

    this.loading = true;
    const content = prompt;

    if (content !== '/clear') this.messages.push({ isSelf: true, content, timestamp: new Date().toISOString() });

    // Force scroll when user sends a message (exit readback mode)
    this._forceScrollToBottom();

    let handled = false;
    if (content.startsWith("/stock")) {
      this.messages.push({
        isSelf: false,
        content: null,
        componentType: StreamWidgetComponent
      })
      this.loading = false;
      setTimeout(() => {
        this.chatScreen.loadDynamicComponents();
      }, 100);

      return;
    }

    if (content.startsWith("/model")) {
      this.messages.push({
        isSelf: false,
        content: null,
        componentType: ModelChartWidgetComponent
      })
      this.loading = false;
      setTimeout(() => {
        this.chatScreen.loadDynamicComponents();
      }, 100);

      return;
    }

    if (content.startsWith("/canvas")) {
      this.messages.push({
        isSelf: false,
        content: null,
        componentType: DefinitionsWidgetComponent
      })
      this.loading = false;
      setTimeout(() => {
        this.chatScreen.loadDynamicComponents();
      }, 100);

      return;
    }

    this.slashCommands.forEach( cmd => {
      const cmdName = cmd.name.replace(/\.[^/.]+$/, "");
      const fullCommand = `/${cmdName}`;
      console.log("fullCommand", fullCommand);
      if (
        content.startsWith(fullCommand) &&
        (content.length === fullCommand.length ||
         (content.length > fullCommand.length && content[fullCommand.length] === ' '))
      ) {
        const args = content.substring(fullCommand.length + 1).trim();
        const newTicker = args.split(' ')[0].trim().toUpperCase();
        if (newTicker) this._ticker = newTicker;

        this.loading = true;
        this.slashCommandActive = true;
        this.activeCommandId = `${this.teamName}:${this._identity.me.username}:${this.uuid}:${cmdName}`;
        this.anchors = [];

        console.log("Calling the slash command ...", cmdName)
        handled = true;
        this._chat.slashAsync(this.teamName, this._identity.me.username, this.uuid, cmdName, { "text": args }).then((response) => {
          this.loading = false;
          this.slashCommandActive = false;
          this.activeCommandId = null;
          console.log(response)
          this.handleIncomingMessage(response);
          this.updateSessions();
        }).catch((err) => {
          this.loading = false;
          this.slashCommandActive = false;
          this.activeCommandId = null;
          console.error(err);

          if (err instanceof HttpErrorResponse) {
            if (err.error?.error === 'Command cancelled by user') {
              this.messages.push({ isSelf: false, content: 'Command cancelled by user.', isError: false });
            } else {
              this.messages.push({ isSelf: false, content: 'Internal Server Error: ' + err.error?.error, isError: true });
            }
          }
        })
        .finally(() => {
          this._scrollToBottom();
          this._focusPromptBox();
        });

      }
    });
    if(handled) {
      return;
    }

    console.log("input not handled by slash commands, submitting to chat", content);

    if (content === '/clear') {
      this.$clear();
    } else {
      // Build unified context envelope
      const messageWithContext = this._buildContextEnvelope(content);

      console.log("Calling lit-mux stream ...", messageWithContext);

      // Reset streaming state before starting a new chat
      this.streamingMessageId = null;
      this.streamingContent = '';
      this.streamingMessageIndex = -1;

      // Clear any existing timeout
      if (this.streamingTimeoutId) {
        clearTimeout(this.streamingTimeoutId);
        this.streamingTimeoutId = null;
      }

      // Set active flags
      this.chatActive = true;
      this.activeChatId = `${this.teamName}:${this._identity.me.username}:${this.uuid}:chat`;

      // Add placeholder for streaming response
      const streamingMessageIndex = this.messages.length;
      this.messages.push({
        isSelf: false,
        content: '',
        isStreaming: true,
        timestamp: new Date().toISOString()
      });
      this.cdr.detectChanges();

      // Stream via lit-mux - throttle UI updates for performance
      let lastUpdateTime = 0;
      let pendingUpdate: string | null = null;
      let updateScheduled = false;

      const throttledUpdate = (fullResponse: string) => {
        const now = Date.now();
        if (now - lastUpdateTime > 50) {  // Update at most every 50ms
          lastUpdateTime = now;
          if (this.messages[streamingMessageIndex]) {
            this.messages[streamingMessageIndex].content = fullResponse;
            this.messages[streamingMessageIndex].isStreaming = true;
            this.chatScreen.refresh();
            this.cdr.detectChanges();
            this._scrollToBottom();
          }
          pendingUpdate = null;
        } else if (!updateScheduled) {
          pendingUpdate = fullResponse;
          updateScheduled = true;
          setTimeout(() => {
            updateScheduled = false;
            if (pendingUpdate !== null) {
              throttledUpdate(pendingUpdate);
            }
          }, 50 - (now - lastUpdateTime));
        } else {
          pendingUpdate = fullResponse;
        }
      };

      this._muxStream.sendStreamingMessage(
        this.currentAgentId,
        this.uuid,
        messageWithContext,
        {
          onContent: (fullResponse: string) => {
            throttledUpdate(fullResponse);
          },
          onError: (error: string) => {
            console.error('Stream error:', error);
            // Network errors (fetch failed, connection reset) likely mean the server
            // restarted. Wait for it to come back, then reload the session to pick
            // up the saved partial response instead of showing an error.
            const isNetworkError = /fetch|network|abort|ERR_CONNECTION|Failed to fetch/i.test(error);
            if (isNetworkError && this.uuid) {
              console.log('Connection lost — waiting for server to recover...');
              if (this.messages[streamingMessageIndex]) {
                this.messages[streamingMessageIndex].content += '\n\n*Reconnecting...*';
                this.messages[streamingMessageIndex].isStreaming = true;
                this.chatScreen.refresh();
                this.cdr.detectChanges();
              }
              this._waitForServerAndReload(streamingMessageIndex);
              return;
            }
            if (this.messages[streamingMessageIndex]) {
              this.messages[streamingMessageIndex].content = `Error: ${error}`;
              this.messages[streamingMessageIndex].isError = true;
              this.messages[streamingMessageIndex].isStreaming = false;
            }
            this.loading = false;
            this.chatActive = false;
            this.chatScreen.refresh();
            this.cdr.detectChanges();
          },
          onComplete: (fullResponse: string) => {
            console.log('Stream complete');
            if (this.messages[streamingMessageIndex]) {
              this.messages[streamingMessageIndex].content = fullResponse;
              this.messages[streamingMessageIndex].isStreaming = false;
            }
            this.loading = false;
            this.chatActive = false;
            this.activeChatId = null;
            this.updateSessions();
            this.chatScreen.refresh();
            this.cdr.detectChanges();
            this._scrollToBottom();
            this._focusPromptBox();
            this._backfillMessageIds();
          },
          onStart: (streamId: string, turnPath?: string) => {
            console.log('Stream started:', streamId, 'turnPath:', turnPath);
            if (turnPath) {
              // Set turn path on assistant message
              if (this.messages[streamingMessageIndex]) {
                this.messages[streamingMessageIndex].turnPath = turnPath;
              }
              // Also set on the preceding user message (same turn file contains both)
              const userMessageIndex = streamingMessageIndex - 1;
              if (userMessageIndex >= 0 && this.messages[userMessageIndex]?.isSelf) {
                this.messages[userMessageIndex].turnPath = turnPath;
              }
            }
          },
          onSystemEvent: (event: string) => {
            this._handleSystemEvent(event);
          }
        }
      );
    }
  }

  updateSessions() {
    console.log('Updating sessions list');
    // Load sessions from lit-mux for the current agent
    this._muxSession.loadSessions(this.currentAgentId).then((muxSessions) => {
      console.log('Received sessions from lit-mux:', muxSessions);

      // Convert lit-mux sessions to the format expected by the UI
      this._rawSessions = muxSessions.map(s => ({
        session: `${this.teamName}:${this.username}:${s.id}`,
        title: s.display_name || s.name || 'New Session',
        mtime: new Date(s.updated_at).getTime()
      })) as ChatService.ChatSession[];

      this.sessions = this.organizeChatSessions();
      this.updateFilteredSessions();
      this.cdr.detectChanges();
      console.log('Sessions updated in UI');

      if (this.uuid === undefined) {
        console.log('update_session', { uuid: this.uuid });
        // Work session override: jump directly to the specified session
        if (this.workSessionId) {
          this.uuid = this.workSessionId;
          this.getHistory().then(() => {
            // Auto-send warm_up prompt if provided (e.g. from a channel-action card)
            if (this.workSessionWarmUp) {
              const warmUp = this.workSessionWarmUp;
              this.workSessionWarmUp = undefined; // Only send once
              this.$submit(warmUp);
            }
          });
        } else if (this.defaultChat === DefaultChat.LastChat && this.lastChat !== undefined) {
          this.uuid = this.lastChat;
          this.getHistory();
        }
        // Otherwise leave uuid undefined - $submit will create a session on first message
      }
    }).catch(err => {
      console.warn('Could not load sessions from lit-mux:', err);
      // Leave uuid undefined - $submit will create a session on first message
    });
  }

  public $clear(): void {
    if (this.uuid !== undefined) return;

    this._chat
      .removeAsync(this.teamName, this._identity.me.username, this.uuid)
      .then((_response) => {
        this.loading = false;
        this.messages.splice(0, this.messages.length);
        this._widget.save(this.teamName);
        this.anchors = [];
      })
      .catch((err) => {
        this.loading = false;
        console.error(err);
      });
  }

  public $changeModel(model: string): void {
    this.modelChange.emit(model);
    this.model = model;
    localStorage.setItem(`${this._storagePrefix}chat-model`, model);

    if (this.currentBackendId === 'claude-interactive') {
      this._http.post(`${ConfigService.muxUrl}/backends/claude-interactive/command`, {
        agent_id: this.currentAgentId,
        command: `/model ${model}`,
      }).subscribe({
        error: (err: any) => console.warn('Model command failed:', err),
      });
    }
  }

  public $clickToggleHistory(): void {
    this.historyOpen = !this.historyOpen;
  }

  public $clickChat(session: ChatService.ChatSession): void {
    this.uuid = session.session.split(':').pop();
    localStorage.setItem(`${this._storagePrefix}last-chat`, this.uuid);
    this.getHistory();

    // Show chat if it was hidden (user clicking a session implies they want to see chat)
    if (this.chatHidden) {
      this.showChat();
    }
  }

  public async $clickNewChat(): Promise<void> {
    try {
      // Get the current agent to find its backend
      const agent = this.availableAgents.find(a => a.id === this.currentAgentId);
      const backends = agent ? [agent.backend] : ['claude'];

      // Create a new session via lit-mux
      const session = await this._muxSession.createSession(this.currentAgentId, backends);
      if (session) {
        this.uuid = session.id;
        this.messages = [];
        this.anchors = [];
        this.hasMoreMessages = false;  // Reset pagination state for new session
        this.title = session.display_name || 'New Session';
        localStorage.setItem(`${this._storagePrefix}last-chat`, this.uuid);
        this.updateSessions();
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Failed to create new chat:', error);
      // Fallback to client-side UUID
      this.uuid = crypto.randomUUID();
      this.messages = [];
      this.anchors = [];
      this.hasMoreMessages = false;  // Reset pagination state for new session
    }
  }

  public async $clickRenameSession(session: ChatService.ChatSession): Promise<void> {
    const uuid = session.session.split(':').pop();
    const newTitle = await this.dialogs.prompt('New Session Name', session.title);
    if (!newTitle || newTitle.trim() === '') return;

    const title = newTitle.trim();

    this.isRenaming = true;
    this.cdr.detectChanges();

    try {
      await this._muxSession.renameSession(uuid, title);
      this._rawSessions = this._rawSessions.map((s) => {
        if (s.session === session.session) s.title = title;
        return s;
      });
      this.sessions = this.organizeChatSessions();
      this.updateFilteredSessions();
    } catch (err) {
      console.error('Error renaming session:', err);
      this.dialogs.alert('Error renaming conversation. Please try again.');
    } finally {
      this.isRenaming = false;
      this.cdr.detectChanges();
    }
  }

  public async $clickDeleteSession(session: ChatService.ChatSession): Promise<void> {
    const uuid = session.session.split(':').pop();

    this.isDeleting = true;
    this.cdr.detectChanges();

    try {
      await this._muxSession.deleteSession(uuid);
      this._rawSessions = this._rawSessions.filter((s) => s.session !== session.session);
      this.sessions = this.organizeChatSessions();
      this.updateFilteredSessions();

      if (this.uuid === uuid) {
        // Only create new chat if no sessions remain, otherwise select the first available
        if (this._rawSessions.length === 0) {
          this.$clickNewChat();
        } else {
          // Select the most recent session
          this.$clickChat(this._rawSessions[0]);
        }
      }
    } catch (err: any) {
      console.error('Error deleting session:', err);
      this.dialogs.alert(err?.status === 423 && err?.error?.detail ? err.error.detail : 'Error deleting conversation. Please try again.');
    } finally {
      this.isDeleting = false;
      this.cdr.detectChanges();
    }
  }

  public $editMCPConfig(): void {
    this.onEditMCPConfig.emit({ path: "/etc/lit/mcp.json" });
  }

  private onPromptClick(topic) {
    this.chatScreen.scrollToAnchor(topic);
  }

  public $clickSample(sample: Sample): void {
    // Set the input in the child component and submit
    this.chatInputComponent?.setInput(sample.text);
    this.$submit(sample.text);
  }

  public $cancelSlashCommand(): void {
    if (this.slashCommandActive && this.activeCommandId) {
      console.log('Cancelling slash command:', this.activeCommandId);
      this._chat.cancelSlashAsync(this.activeCommandId).then(() => {
        this.loading = false;
        this.slashCommandActive = false;
        this.activeCommandId = null;
        this.messages.push({ isSelf: false, content: 'Command cancelled by user.', isError: false });
        this._scrollToBottom();
      }).catch(err => {
        console.error('Error cancelling command:', err);
        // Keep the UI responsive even if cancellation fails
        this.loading = false;
        this.slashCommandActive = false;
        this.activeCommandId = null;
      });
    }
  }

  public $cancelChat(): void {
    console.log('$cancelChat() called, chatActive=', this.chatActive, 'agentId=', this.currentAgentId);
    if (this.chatActive) {
      console.log('Cancelling chat');

      // Clear any streaming timeout
      if (this.streamingTimeoutId) {
        clearTimeout(this.streamingTimeoutId);
        this.streamingTimeoutId = null;
      }

      const resetUi = () => {
        this.loading = false;
        this.chatActive = false;
        this.activeChatId = null;
        this.streamingMessageId = null;
        this.streamingContent = '';
        this.streamingMessageIndex = -1;
        this._scrollToBottom();
        this.cdr.detectChanges();
      };

      // Cancel via agent endpoint (works for both channel and direct streams)
      this._http.post(`${ConfigService.muxUrl}/agents/${this.currentAgentId}/cancel`, {}).subscribe({
        next: () => { console.log('Agent generation cancelled'); },
        error: (err: any) => { console.error('Error cancelling agent generation:', err); },
      });

      // Also cancel via mux-stream service (for direct chat sessions)
      this._muxStream.cancelGeneration().then(resetUi).catch(err => {
        console.error('Error cancelling chat:', err);
        resetUi();
      });
    }
  }

  public $toggleSafeMode(): void {
    const newState = !this.safeMode;
    const sessionId = this.uuid;
    if (!sessionId) return;

    fetch(`/mux/api/sessions/${sessionId}/safe_mode`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: newState }),
    }).then(res => res.json()).then(data => {
      this.safeMode = data.safe_mode;
      this.cdr.detectChanges();
    }).catch(err => {
      console.error('Error toggling safe mode:', err);
    });
  }

  /**
   * Starts or resets the streaming timeout
   * Called when streaming begins and whenever a token is received
   */
  private startStreamingTimeout(): void {
    // Clear any existing timeout first
    if (this.streamingTimeoutId) {
      clearTimeout(this.streamingTimeoutId);
      this.streamingTimeoutId = null;
    }

    // Create a new timeout - first for the warning message
    this.streamingTimeoutId = setTimeout(() => {
      // Only show warning if we're still streaming
      if (this.chatActive && this.activeChatId === `${this.teamName}:${this._identity.me.username}:${this.uuid}:chat`) {
        console.log('Streaming timeout reached without recent tokens');

        // Add warning but don't reset streaming state yet
        this.messages.push({
          isSelf: false,
          content: 'This response is taking longer than expected. The connection will remain open, but you may want to try again if no more content appears.',
          isError: false,
          lineBreak: true
        });

        this.cdr.detectChanges();
        this._scrollToBottom();

        // Set a secondary timeout to auto-terminate streaming if nothing happens
        // This ensures we don't stay in streaming mode forever
        this.streamingTimeoutId = setTimeout(() => {
          // If we're still in streaming mode after the second timeout
          if (this.chatActive && this.streamingMessageId) {
            console.log('No stream activity for extended period - auto-closing stream');

            // Simulate stream-end event to clean up UI state
            const autoEndEvent: ChatService.StreamEvent = {
              type: 'stream-end',
              session: this.session,
              message_id: this.streamingMessageId,
              content: '',
              timestamp: new Date().toISOString(),
              origin: 'chat',
              team: this.teamName
            };

            // Handle the auto-generated stream-end event
            this.handleStreamEvent(autoEndEvent);

            // Add a note that we auto-completed the stream
            this.messages.push({
              isSelf: false,
              content: 'No more content received. Stream automatically closed.',
              isError: false,
              lineBreak: true
            });

            this.cdr.detectChanges();
            this._scrollToBottom();
          }
        }, 15000); // Additional 15 seconds after the warning
      }
    }, 15000); // 15 second timeout for initial warning
  }

  // Methods for menubar functionality in full-screen mode
  public toggleDesktopEnvironment(): void {
    this._settings.desktopEnvironment = !this._settings.desktopEnvironment;
  }

  public get isDarkTheme(): boolean {
    return this._settings.theme === 'dark';
  }

  public toggleTheme(): void {
    this._settings.theme = this._settings.theme === 'dark' ? 'light' : 'dark';
  }

  public async openWidget(widgetName: string): Promise<void> {
    // If in full-screen mode, open in app pane
    if (this.isFullScreen) {
      console.log('Opening widget in app pane:', widgetName);
      this.openApp(widgetName as WidgetService.WidgetName);
    } else {
      // Grid mode - open widget on the grid
      const widget = this._widget.createWidget(widgetName as WidgetService.WidgetName, this.teamName, undefined, {
        outputs: {
          onClose: ($event: BaseWidgetComponent.Event) => {
            setTimeout(() => this._widget.removeWidget(this._identity.me.selectedTeamName, $event.id));
          },
          onRestore: ($event: BaseWidgetComponent.Event) => {
            this._widget.restoreWidget(this._identity.me.selectedTeamName, $event.id);
          },
          onMaximize: ($event: BaseWidgetComponent.Event) => {
            this._widget.maximizeWidget(this._identity.me.selectedTeamName, $event.id);
          },
          onToggle: ($event: BaseWidgetComponent.Event) => {
            this._widget.toggleWidget(this._identity.me.selectedTeamName, $event.id);
          }
        }
      });
      this._widget.addWidget(this._identity.me.selectedTeamName, widget);
    }
  }

  // ============================================================================
  // App Pane Management (full-screen mode)
  // ============================================================================

  /** Map of widget names to their icons */
  private readonly WIDGET_ICONS: Record<string, string> = {
    'FilesWidget': 'folder',
    'TerminalWidget': 'terminal',
    'SettingsWidget': 'settings',
    'MonacoWidget': 'code',
    'MarkdownWidget': 'description',
    'AgentSettingsWidget': 'smart_toy',
    'BackendDetailWidget': 'cloud',
    'HostsWidget': 'dns',
    'GPUsWidget': 'developer_board',
    'StreamsWidget': 'air',
    'DeploysWidget': 'launch',
    'RawDataWidget': 'cloud_upload',
    'FeatureSelectorWidget': 'label',
    'BuildsWidget': 'handyman',
    'AssetsWidget': 'network_check',
    'DefinitionsWidget': 'account_tree',
    'ExperimentsWidget': 'model_training',
    'VaultWidget': 'key',
    'PromptsWidget': 'edit_note',
    'LLMWorkflowsWidget': 'account_tree',
    'JobsWidget': 'schedule',
    'IframeWidget': 'web',
    'BrowserWidget': 'language',
  };

  /** Open an app in the app pane (or switch to it if already open) */
  public openApp(widgetName: WidgetService.WidgetName): void {
    // Check if already open
    const existingApp = this.openApps.find(app => app.widgetName === widgetName);
    if (existingApp) {
      // Switch to it
      this.activeAppId = existingApp.id;
      this.appPaneHidden = false; // Show app pane if it was hidden (triggers save)
      this.cdr.detectChanges();
      return;
    }

    // Create new app
    const appId = `app-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const appName = widgetName.replace('Widget', '');
    const icon = this.WIDGET_ICONS[widgetName] || 'extension';

    const app: OpenApp = {
      id: appId,
      name: appName,
      widgetName: widgetName,
      icon: icon
    };

    this.openApps.push(app);
    this.activeAppId = appId;
    this.appPaneHidden = false; // Show app pane when opening an app (triggers save)
    this.saveAppPaneState(); // Save the new app
    this.cdr.detectChanges();
    console.log('Opened app:', app);
  }

  /** Open an app with custom inputs (e.g., agentId for AgentSettingsWidget, url/title for IframeWidget) */
  public openAppWithInputs(widgetName: WidgetService.WidgetName, customInputs: Record<string, any>): void {
    // Determine how to match existing apps based on widget type
    // - IframeWidget: match by URL (allow multiple different iframes)
    // - MonacoWidget/MarkdownWidget: match by path (allow multiple different files)
    // - ExternalAppWidget: match by app ID (allow multiple different external apps)
    // - Others: match by widget name only (single instance)
    let existingApp: OpenApp | undefined;
    if (widgetName === 'IframeWidget') {
      existingApp = this.openApps.find(app => app.widgetName === widgetName && app.customInputs?.url === customInputs.url);
    } else if (widgetName === 'MonacoWidget' || widgetName === 'MarkdownWidget' || widgetName === 'PreviewWidget') {
      // Check both 'path' and 'filePath' for compatibility - MarkdownWidget uses filePath, others use path
      const inputPath = customInputs.path || customInputs.filePath;
      existingApp = this.openApps.find(app => {
        const existingPath = app.customInputs?.path || app.customInputs?.filePath;
        return app.widgetName === widgetName && existingPath === inputPath;
      });
    } else if (widgetName === 'ExternalAppWidget') {
      existingApp = this.openApps.find(app => app.widgetName === widgetName && app.customInputs?.app?.id === customInputs.app?.id);
    } else {
      existingApp = this.openApps.find(app => app.widgetName === widgetName);
    }

    if (existingApp) {
      // Switch to existing app
      this.activeAppId = existingApp.id;
      this.appPaneHidden = false;
      this.cdr.detectChanges();
      return;
    }

    // Create new app with custom inputs
    const appId = `app-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Use custom title for IframeWidget, otherwise derive from widget name
    const appName = customInputs.title || widgetName.replace('Widget', '');
    // Use custom icon if provided (e.g., from dynamic widget config)
    const icon = customInputs.icon || this.WIDGET_ICONS[widgetName] || 'extension';

    const app: OpenApp = {
      id: appId,
      name: appName,
      widgetName: widgetName,
      icon: icon,
      customInputs: customInputs
    };

    this.openApps.push(app);
    this.activeAppId = appId;
    this.appPaneHidden = false;
    this.saveAppPaneState();
    this.cdr.detectChanges();
    console.log('Opened app with inputs:', app);
  }

  /** Close an app by ID */
  public closeApp(appId: string): void {
    const index = this.openApps.findIndex(app => app.id === appId);
    if (index === -1) return;

    this.openApps.splice(index, 1);

    // If we closed the active app, switch to another or clear
    if (this.activeAppId === appId) {
      if (this.openApps.length > 0) {
        // Switch to the previous app, or the first one
        const newIndex = Math.min(index, this.openApps.length - 1);
        this.activeAppId = this.openApps[newIndex].id;
      } else {
        this.activeAppId = null;
      }
    }

    this.saveAppPaneState(); // Save after closing
    this.cdr.detectChanges();
    console.log('Closed app:', appId, 'Active now:', this.activeAppId);
  }

  /** Switch to a specific app */
  public switchToApp(appId: string): void {
    if (this.openApps.some(app => app.id === appId)) {
      this.activeAppId = appId;
      this.appPaneHidden = false; // Show app pane when switching (triggers save)
      this.saveAppPaneState(); // Save active app change
      this.cdr.detectChanges();
    }
  }

  /** Hide the app pane (VS Code style - collapse but keep apps open) */
  public hideAppPane(): void {
    this.appPaneHidden = true;
    this.cdr.detectChanges();
  }

  /** Show the app pane (unhide it) */
  public unhideAppPane(): void {
    this.appPaneHidden = false;
    this.cdr.detectChanges();
  }

  /** Hide the chat area */
  public hideChat(): void {
    this.chatHidden = true;
    this.cdr.detectChanges();
  }

  /** Show the chat area */
  public showChat(): void {
    this.chatHidden = false;
    this.cdr.detectChanges();
  }

  // ============================================================================
  // App Pane State Persistence
  // ============================================================================

  private _appPaneStateDebounceTimer: any = null;

  /** Save app pane state to localStorage (debounced) */
  private saveAppPaneState(): void {
    if (!this.isFullScreen || !this.teamName) return;

    // Debounce saves to avoid excessive writes during resize drags
    if (this._appPaneStateDebounceTimer) {
      clearTimeout(this._appPaneStateDebounceTimer);
    }
    this._appPaneStateDebounceTimer = setTimeout(() => {
      this._saveAppPaneStateImmediate();
    }, 200);
  }

  private _saveAppPaneStateImmediate(): void {
    const state = {
      openApps: this.openApps.map(app => ({
        widgetName: app.widgetName,
        name: app.name,
        icon: app.icon,
        customInputs: app.customInputs  // Preserve URL and other custom inputs
      })),
      activeWidgetName: this.activeAppId
        ? this.openApps.find(a => a.id === this.activeAppId)?.widgetName
        : null,
      appPaneHeight: this._appPaneHeight,
      appPaneHidden: this._appPaneHidden,
      chatHidden: this._chatHidden,
      sidebarWidth: this._sidebarWidth,
      pinsPanePercent: this._pinsPanePercent
    };
    localStorage.setItem(`${this._storagePrefix}chat-app-pane-state`, JSON.stringify(state));
  }

  /** Restore app pane state from localStorage */
  private restoreAppPaneState(): void {
    if (!this.isFullScreen || !this.teamName) return;

    const saved = localStorage.getItem(`${this._storagePrefix}chat-app-pane-state`);
    if (!saved) return;

    try {
      const state = JSON.parse(saved);

      // Restore layout settings (use private fields to avoid triggering saves)
      if (typeof state.appPaneHeight === 'number') {
        this._appPaneHeight = state.appPaneHeight;
      }
      if (typeof state.appPaneHidden === 'boolean') {
        this._appPaneHidden = state.appPaneHidden;
      }
      if (typeof state.chatHidden === 'boolean') {
        this._chatHidden = state.chatHidden;
      }
      if (typeof state.sidebarWidth === 'number') {
        this._sidebarWidth = state.sidebarWidth;
      }
      if (typeof state.pinsPanePercent === 'number') {
        this._pinsPanePercent = Math.max(15, Math.min(50, state.pinsPanePercent));
      } else if (typeof state.pinsPaneWidth === 'number') {
        this._pinsPanePercent = 30;
      }

      // Restore open apps
      if (Array.isArray(state.openApps)) {
        this.openApps = state.openApps.map((savedApp: any) => {
          // Reset 'loaded' flag on external apps - scripts need to be reloaded after page refresh
          const customInputs = savedApp.customInputs ? { ...savedApp.customInputs } : undefined;
          if (customInputs?.app) {
            customInputs.app = { ...customInputs.app, loaded: false };
          }

          return {
            id: `app-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            widgetName: savedApp.widgetName,
            name: savedApp.name,
            icon: savedApp.icon || this.WIDGET_ICONS[savedApp.widgetName] || 'extension',
            customInputs
          };
        });

        // Restore active app
        if (state.activeWidgetName && this.openApps.length > 0) {
          const activeApp = this.openApps.find(a => a.widgetName === state.activeWidgetName);
          this.activeAppId = activeApp ? activeApp.id : this.openApps[0].id;
        } else if (this.openApps.length > 0) {
          this.activeAppId = this.openApps[0].id;
        }
      }

      console.log('Restored app pane state:', state);
    } catch (err) {
      console.error('Error restoring app pane state:', err);
    }
  }

  /** Get the widget component type for an app */
  public getWidgetComponent(widgetName: WidgetService.WidgetName): any {
    return WidgetService.REGISTRY[widgetName];
  }

  /** Get the inputs for a widget in the app pane */
  public getWidgetInputs(app: OpenApp): Record<string, any> {
    const inputs: Record<string, any> = {
      teamName: this.teamName
    };

    // Signal to widget that it's embedded in the app pane (all widgets inherit this from BaseWidgetComponent)
    inputs.isEmbedded = true;

    // Pass agentId to AgentSettingsWidget so it knows which agent to edit
    // Use customInputs if provided (e.g., null for new agent), otherwise use currentAgentId
    if (app.widgetName === 'AgentSettingsWidget') {
      const rawAgentId = app.customInputs?.hasOwnProperty('agentId')
        ? app.customInputs.agentId
        : this.currentAgentId;
      // Resolve a possibly-stale / name-slug id (e.g. 'claude' for the default agent
      // whose display name is "Claude") to the real agent id. currentAgentId can drift
      // to a slug via unvalidated paths; opening the widget with it queries a phantom
      // agent and the tabs (Responsibilities etc.) render empty.
      inputs.agentId = this._resolveAgentId(rawAgentId);
      inputs.editFileCallback = (path: string) => this.openFileInEditor(path);
    }

    // FilesWidget: pass callbacks for file actions (outputs don't work with ngComponentOutlet)
    if (app.widgetName === 'FilesWidget') {
      inputs.editFileCallback = (path: string) => this.openFileInEditor(path);
      inputs.viewMarkdownCallback = (path: string, title: string) => this.openMarkdownViewer(path, title);
      inputs.viewCsvCallback = (path: string, title: string) => this.openCsvViewer(path, title);
      inputs.previewFileCallback = (path: string, previewType: 'image' | 'pdf') => this.openPreviewFile(path, previewType);
    }

    // JobsWidget: pass callback for opening job files
    if (app.widgetName === 'JobsWidget') {
      inputs.editFileCallback = (path: string) => this.openFileInEditor(path);
    }

    // Merge custom inputs, but filter out 'icon' and 'title' which are for the tab, not the widget
    if (app.customInputs) {
      const { icon, title, ...widgetInputs } = app.customInputs;
      Object.assign(inputs, widgetInputs);
    }

    return inputs;
  }

  /** Start dragging the resize divider */
  private _dividerDragging = false;
  private _dividerStartY = 0;
  private _dividerStartHeight = 0;

  public onDividerDragStart(event: MouseEvent, container: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    this._dividerDragging = true;
    this._dividerStartY = event.clientY;
    this._dividerStartHeight = this.appPaneHeight;

    const containerRect = container.getBoundingClientRect();

    // Disable pointer events on iframes during drag (they swallow mouseup events)
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => (iframe.style.pointerEvents = 'none'));

    // Add document-level listeners for drag
    const onMouseMove = (e: MouseEvent) => {
      if (!this._dividerDragging) return;

      const mouseY = e.clientY - containerRect.top;
      const newHeightPercent = (mouseY / containerRect.height) * 100;

      this.appPaneHeight = Math.max(15, Math.min(85, newHeightPercent));
      this.cdr.detectChanges();
    };

    const cleanup = () => {
      this._dividerDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', cleanup);
      window.removeEventListener('blur', cleanup);
      // Re-enable pointer events on iframes
      iframes.forEach(iframe => (iframe.style.pointerEvents = ''));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('blur', cleanup);  // Handle mouse released outside window
  }

  /** Start dragging the sidebar resize divider */
  private _sidebarDragging = false;

  public onSidebarDragStart(event: MouseEvent, container: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    this._sidebarDragging = true;

    const containerRect = container.getBoundingClientRect();

    // Disable pointer events on iframes during drag (they swallow mouseup events)
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => (iframe.style.pointerEvents = 'none'));

    const onMouseMove = (e: MouseEvent) => {
      if (!this._sidebarDragging) return;

      const mouseX = e.clientX - containerRect.left;
      // Snap closed when dragged below threshold
      if (mouseX < 80) {
        this.historyOpen = false;
        cleanup();
        this.cdr.detectChanges();
        return;
      }
      this.sidebarWidth = Math.max(150, Math.min(500, mouseX));
      this.cdr.detectChanges();
    };

    const cleanup = () => {
      this._sidebarDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', cleanup);
      window.removeEventListener('blur', cleanup);
      // Re-enable pointer events on iframes
      iframes.forEach(iframe => (iframe.style.pointerEvents = ''));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('blur', cleanup);  // Handle mouse released outside window
  }

  private _pinsPaneDragging = false;

  public onPinsPaneDragStart(event: MouseEvent, container: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    this._pinsPaneDragging = true;

    const containerRect = container.getBoundingClientRect();

    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => (iframe.style.pointerEvents = 'none'));

    const onMouseMove = (e: MouseEvent) => {
      if (!this._pinsPaneDragging) return;

      const mouseX = e.clientX - containerRect.left;
      const pct = ((containerRect.width - mouseX) / containerRect.width) * 100;
      this.pinsPanePercent = pct;
      this.cdr.detectChanges();
    };

    const cleanup = () => {
      this._pinsPaneDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', cleanup);
      window.removeEventListener('blur', cleanup);
      iframes.forEach(iframe => (iframe.style.pointerEvents = ''));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('blur', cleanup);
  }

  public openSettings(): void {
    // In full-screen mode, render settings inline in chat
    if (this.isFullScreen) {
      this.openWidget('SettingsWidget');
      return;
    }

    const widget = this._widget.createWidget('SettingsWidget', this.teamName, undefined, {
      defaultSpace: { minItemRows: 16, minItemCols: 27 },
      outputs: {
        onClose: ($event: BaseWidgetComponent.Event) => {
          setTimeout(() => this._widget.removeWidget(this._identity.me.selectedTeamName, $event.id));
        },
        onRestore: ($event: BaseWidgetComponent.Event) => {
          this._widget.restoreWidget(this._identity.me.selectedTeamName, $event.id);
        },
        onMaximize: ($event: BaseWidgetComponent.Event) => {
          this._widget.maximizeWidget(this._identity.me.selectedTeamName, $event.id);
        },
        onToggle: ($event: BaseWidgetComponent.Event) => {
          this._widget.toggleWidget(this._identity.me.selectedTeamName, $event.id);
        }
      }
    });
    this._widget.addWidget(this._identity.me.selectedTeamName, widget);
  }

  private static IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico']);

  /**
   * Route a file open by extension to the appropriate viewer.
   */
  public openFileByExtension(path: string, title: string): void {
    const ext = path.split('.').pop()?.toLowerCase() || '';
    if (ext === 'md') {
      this.openMarkdownViewer(path, title);
    } else if (ext === 'csv') {
      this.openCsvViewer(path, title);
    } else if (ext === 'pdf') {
      this.openPreviewFile(path, 'pdf');
    } else if (ChatWidgetComponent.IMAGE_EXTENSIONS.has(ext)) {
      this.openPreviewFile(path, 'image');
    } else {
      this.openFileInEditor(path);
    }
  }

  /**
   * Open a file in the Monaco editor widget (for text/code files)
   */
  public async openFileInEditor(filePath: string): Promise<void> {
    const fileName = filePath.split('/').pop() || 'file';

    if (this.isFullScreen) {
      // In fullscreen mode, open MonacoWidget as a tab in the app pane
      this.openAppWithInputs('MonacoWidget' as WidgetService.WidgetName, {
        path: filePath,
        title: fileName,
        icon: 'code'
      });
    } else if (this._widget.grid?.isDockviewMode) {
      this._widget.grid.$editFile({ parentId: null, path: filePath });
    } else {
      // Grid mode - open MonacoWidget on the grid
      const widget = this._widget.createWidget('MonacoWidget', this.teamName, undefined, {
        baseInputs: { path: filePath, title: fileName },
        outputs: {
          onClose: ($event: BaseWidgetComponent.Event) => {
            setTimeout(() => this._widget.removeWidget(this._identity.me.selectedTeamName, $event.id));
          }
        }
      });
      this._widget.addWidget(this._identity.me.selectedTeamName, widget);
    }
  }

  /**
   * Open a markdown file in the MarkdownWidget viewer (or blank if no path)
   */
  public async openMarkdownViewer(path: string, title: string, startInEditMode: boolean = false): Promise<void> {
    const widgetTitle = title || (path ? path.split('/').pop() : 'New Document') || 'markdown';
    const inputs: any = { title: widgetTitle, icon: 'description' };

    if (path) {
      inputs.src = `${ConfigService.muxUrl}/commander/file?path=${encodeURIComponent(path)}`;
      inputs.filePath = path;
    }

    if (startInEditMode) {
      inputs.startInEditMode = true;
    }

    if (this.isFullScreen) {
      // In fullscreen mode, open MarkdownWidget as a tab in the app pane
      this.openAppWithInputs('MarkdownWidget' as WidgetService.WidgetName, inputs);
    } else if (this._widget.grid?.isDockviewMode) {
      this._widget.grid.$viewMarkdown({ parentId: null, path, title: widgetTitle });
    } else {
      // Grid mode - open MarkdownWidget on the grid
      const widget = this._widget.createWidget('MarkdownWidget', this.teamName, undefined, {
        baseInputs: inputs,
        outputs: {
          onClose: ($event: BaseWidgetComponent.Event) => {
            setTimeout(() => this._widget.removeWidget(this._identity.me.selectedTeamName, $event.id));
          }
        }
      });
      this._widget.addWidget(this._identity.me.selectedTeamName, widget);
    }
  }

  /**
   * Open a blank markdown editor in edit mode
   */
  public async openMarkdownEditorWithPrompt(): Promise<void> {
    this.openMarkdownViewer('', 'New Document', true);
  }

  /**
   * Open a CSV file in the DataViewerWidget (spreadsheet view)
   */
  public async openCsvViewer(path: string, title: string): Promise<void> {
    const widgetTitle = title || path.split('/').pop() || 'CSV';
    const inputs: any = { path, fileType: 'csv', directPath: path, title: widgetTitle, icon: 'table_chart' };

    if (this.isFullScreen) {
      this.openAppWithInputs('DataViewerWidget' as WidgetService.WidgetName, inputs);
    } else if (this._widget.grid?.isDockviewMode) {
      this._widget.grid.$viewCsv({ parentId: null, path, title: widgetTitle });
    } else {
      const widget = this._widget.createWidget('DataViewerWidget', this.teamName, undefined, {
        baseInputs: inputs,
        outputs: {
          onClose: ($event: BaseWidgetComponent.Event) => {
            setTimeout(() => this._widget.removeWidget(this._identity.me.selectedTeamName, $event.id));
          }
        }
      });
      this._widget.addWidget(this._identity.me.selectedTeamName, widget);
    }
  }

  /**
   * Open an image or PDF file in the PreviewWidget
   */
  public async openPreviewFile(path: string, previewType: 'image' | 'pdf'): Promise<void> {
    const fileName = path.split('/').pop() || 'preview';
    const icon = previewType === 'pdf' ? 'picture_as_pdf' : 'image';

    if (this.isFullScreen) {
      // In fullscreen mode, open PreviewWidget as a tab in the app pane
      this.openAppWithInputs('PreviewWidget' as WidgetService.WidgetName, {
        path,
        previewType,
        title: fileName,
        icon
      });
    } else if (this._widget.grid?.isDockviewMode) {
      this._widget.grid.$previewFile({ parentId: null, path, previewType });
    } else {
      // Grid mode - open PreviewWidget on the grid
      const widget = this._widget.createWidget('PreviewWidget', this.teamName, undefined, {
        baseInputs: { path, previewType, title: fileName },
        outputs: {
          onClose: ($event: BaseWidgetComponent.Event) => {
            setTimeout(() => this._widget.removeWidget(this._identity.me.selectedTeamName, $event.id));
          }
        }
      });
      this._widget.addWidget(this._identity.me.selectedTeamName, widget);
    }
  }

  public navigateToTeams(): void {
    this.router.navigate(['/teams']);
  }

  /** Quick-switch to a different team */
  public selectTeam(team: { id: string; name: string; slug: string }): void {
    if (this._identity.me.selectedTeamName === team.slug) return;

    localStorage.setItem('lastTeamSelected', team.slug);
    this._identity.setTeam(team.slug);
    this._identity.setProject(null);

    // Save preference server-side for cross-device sync
    this._userPrefs.setPreferredTeam(team.slug).subscribe();

    this._router.navigate([`/${team.slug}/chat`]);
  }

  private loadFavoriteTeams(): void {
    try {
      const stored = localStorage.getItem(ChatWidgetComponent.FAVORITE_TEAMS_KEY);
      if (stored) {
        const teams = JSON.parse(stored) as string[];
        this.favoriteTeams = new Set(teams);
      }
    } catch (e) {
      console.warn('Failed to load favorite teams:', e);
    }
  }

  /** Load dynamic widgets for the current team */
  private async loadDynamicWidgets(): Promise<void> {
    try {
      this.dynamicWidgets = await this._dynamicWidgets.getWidgets(this.teamName);
      console.log('Loaded dynamic widgets for team', this.teamName, ':', this.dynamicWidgets);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to load dynamic widgets:', error);
    }
  }

  /** Open a dynamic widget (iframe or component) in the app pane */
  public openDynamicWidget(widget: DynamicWidget): void {
    if (widget.type === 'iframe' && widget.url) {
      // Open iframe widget with URL, title, and icon as custom inputs
      this.openAppWithInputs('IframeWidget' as WidgetService.WidgetName, {
        url: widget.url,
        title: widget.title,
        icon: widget.icon || 'web'
      });
    } else if (widget.type === 'component' && widget.element) {
      // Open web component app via ExternalAppWidget
      this.openAppWithInputs('ExternalAppWidget' as WidgetService.WidgetName, {
        // Pass the widget data as an ExternalApp-like object
        app: {
          id: widget.id,
          manifest: {
            name: widget.name,
            title: widget.title,
            icon: widget.icon,
            element: widget.element,
            scripts: widget.scripts || [],
            styles: widget.styles || [],
            config: widget.config
          },
          source: widget.source,
          basePath: widget.basePath,
          loaded: false
        },
        title: widget.title,
        icon: widget.icon || 'extension'
      });
    } else {
      console.warn('Unsupported dynamic widget type:', widget.type);
    }
  }

  // ============================================================================
  // Pinned Apps (fullscreen toolbar)
  // ============================================================================

  /** Check if a built-in widget is pinned */
  public isPinnedApp(widgetName: string): boolean {
    return this.pinnedWidgets.has(widgetName);
  }

  /** Check if a dynamic widget is pinned */
  public isPinnedDynamicWidget(widgetId: string): boolean {
    return this.pinnedDynamicWidgetIds.has(widgetId);
  }

  /** Toggle pin state for a built-in widget */
  public togglePinApp(widgetName: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.pinnedWidgets.has(widgetName)) {
      this.pinnedWidgets.delete(widgetName);
    } else {
      this.pinnedWidgets.add(widgetName);
    }
    this.savePinnedApps();
    this.cdr.detectChanges();
  }

  /** Toggle pin state for a dynamic widget */
  public togglePinDynamicWidget(widget: DynamicWidget, event: MouseEvent): void {
    event.stopPropagation();
    if (this.pinnedDynamicWidgetIds.has(widget.id)) {
      this.pinnedDynamicWidgetIds.delete(widget.id);
    } else {
      this.pinnedDynamicWidgetIds.add(widget.id);
    }
    this.savePinnedDynamicWidgets();
    this.cdr.detectChanges();
  }

  /** Open a pinned app from toolbar */
  public openPinnedApp(pinned: { type: 'widget' | 'dynamic'; widgetName?: string; dynamicWidget?: DynamicWidget }): void {
    if (pinned.type === 'widget' && pinned.widgetName) {
      if (pinned.widgetName === 'AgentSettingsWidget') {
        this.openAgentSettings();
      } else if (pinned.widgetName === 'SettingsWidget') {
        this.openSettings();
      } else {
        this.openWidget(pinned.widgetName as WidgetService.WidgetName);
      }
    } else if (pinned.type === 'dynamic' && pinned.dynamicWidget) {
      this.openDynamicWidget(pinned.dynamicWidget);
    }
  }

  private loadPinnedApps(): void {
    try {
      const stored = localStorage.getItem(ChatWidgetComponent.PINNED_APPS_KEY);
      if (stored) {
        const widgets = JSON.parse(stored) as string[];
        this.pinnedWidgets = new Set(widgets);
      }
    } catch (e) {
      console.warn('Failed to load pinned apps:', e);
    }
  }

  private loadPinnedDynamicWidgets(): void {
    try {
      const stored = localStorage.getItem(ChatWidgetComponent.PINNED_DYNAMIC_WIDGETS_KEY);
      if (stored) {
        const widgetIds = JSON.parse(stored) as string[];
        this.pinnedDynamicWidgetIds = new Set(widgetIds);
      }
    } catch (e) {
      console.warn('Failed to load pinned dynamic widgets:', e);
    }
  }

  private savePinnedApps(): void {
    try {
      const widgets = Array.from(this.pinnedWidgets);
      localStorage.setItem(ChatWidgetComponent.PINNED_APPS_KEY, JSON.stringify(widgets));
    } catch (e) {
      console.warn('Failed to save pinned apps:', e);
    }
  }

  private savePinnedDynamicWidgets(): void {
    try {
      const widgetIds = Array.from(this.pinnedDynamicWidgetIds);
      localStorage.setItem(ChatWidgetComponent.PINNED_DYNAMIC_WIDGETS_KEY, JSON.stringify(widgetIds));
    } catch (e) {
      console.warn('Failed to save pinned dynamic widgets:', e);
    }
  }

  public openUserManager(): void {
    window.location.href = `${ConfigService.keycloakUrl}/admin/${ConfigService.keycloakRealm}/console/#/${ConfigService.keycloakRealm}/users`;
  }

  public logout(): void {
    localStorage.removeItem('id_token');
    localStorage.removeItem('me');
    const logoutUrl = `${ConfigService.keycloakUrl}/realms/${ConfigService.keycloakRealm}/protocol/openid-connect/logout?client_id=${ConfigService.keycloakClientId}&post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
    window.location.href = logoutUrl;
  }

  /**
   * Respond to input property changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isFullScreen) {
      // Only act if the value actually changed (not on first change or same value)
      const change = changes.isFullScreen;
      if (change.isFirstChange() || change.previousValue === change.currentValue) return;

      console.log('isFullScreen changed:', change.currentValue);

      // If toggling to full screen mode, get version for the changelog
      if (change.currentValue && !change.previousValue) {
        this._version.get().then(({ version }) => {
          this.version = version;
        });
      }

      // Force change detection to update the attribute
      this.cdr.detectChanges();
    }
    // Note: isDocked is handled via the setter override, not ngOnChanges

    if (changes['teamName'] && !changes['teamName'].firstChange) {
      this.loadAgents();
    }
  }

  private handleStreamEvent(data: ChatService.StreamEvent) {
    // Only process events for our session
    if (data.session !== this.session) {
      return;
    }

    // Handle the streaming event based on its type
    switch (data.type) {
      case 'stream-start':
        console.log('Stream started:', data.message_id);
        // Initialize a new streaming message
        this.streamingMessageId = data.message_id;
        this.streamingContent = '';
        this.loading = true;
        this.chatActive = true;
        this.activeChatId = `${this.teamName}:${this._identity.me.username}:${this.uuid}:chat`;

        // Add the streaming message to the chat
        this.messages.push({
          isSelf: false,
          content: '',  // Start with empty string
          model: data.model,
          timestamp: data.timestamp,
          isStreaming: true  // Mark as streaming
        });
        this.streamingMessageIndex = this.messages.length - 1;

        // Start the streaming timeout
        this.startStreamingTimeout();

        // Update UI
        this.cdr.detectChanges();
        break;

      case 'stream-token':
        // Always log token events with message ID for debugging
        console.debug('Stream token received for:', data.message_id);

        // Reset the timeout - we're actively receiving data
        this.startStreamingTimeout();

        // Check if we have an active streaming message
        if (!this.streamingMessageId) {
          // If we don't have an active streaming ID but we're receiving tokens,
          // this could be because our timeout fired but streaming is still active
          console.log('No active streaming message but token received. Recovering...');

          // Attempt recovery - create a new streaming message or append to last message
          this.streamingMessageId = data.message_id;

          // Check if the last message looks like it was from streaming
          const lastMessage = this.messages[this.messages.length - 1];
          if (lastMessage && !lastMessage.isSelf) {
            // Assume this is our streaming message that got interrupted
            this.streamingMessageIndex = this.messages.length - 1;
            // Get existing content if any
            this.streamingContent = typeof lastMessage.content === 'string'
              ? lastMessage.content
              : '';
            // Mark as streaming again
            lastMessage.isStreaming = true;
          } else {
            // Create a new streaming message
            this.messages.push({
              isSelf: false,
              content: '',
              model: data.model,
              timestamp: data.timestamp || new Date().toISOString(),
              isStreaming: true
            });
            this.streamingMessageIndex = this.messages.length - 1;
            this.streamingContent = '';
          }

          // Ensure UI flags are set correctly
          this.loading = true;
          this.chatActive = true;
          this.activeChatId = `${this.teamName}:${this._identity.me.username}:${this.uuid}:chat`;
        }

        // Now handle the token
        if (this.streamingMessageId !== data.message_id) {
          console.log('Message ID mismatch, updating to new ID:', data.message_id);
          // Update to the new message ID
          this.streamingMessageId = data.message_id;
        }

        // Add the token to the streaming content
        if (data.content) {
          this.streamingContent += data.content;

          // Update the current message content
          if (this.streamingMessageIndex >= 0) {
            // Strip LINKS: footer during streaming so tags render in the footer, not inline
            const { content: displayContent, links } = ChatScreenComponent.extractLinks(this.streamingContent);
            if (links.length) this.messages[this.streamingMessageIndex].links = links;

            // If content has tool calls, pass raw string so hasToolCalls() works
            // and <app-tool-message> handles rendering
            if (this.chatScreen.hasToolCalls(displayContent)) {
              this.messages[this.streamingMessageIndex].content = displayContent;
            } else {
              // Replace newlines with <br> for proper display in innerHTML
              const formattedContent = displayContent.replace(/\n/g, '<br>');
              // Sanitize the HTML content to prevent Angular errors
              const safeContent = this.sanitizer.bypassSecurityTrustHtml(formattedContent);
              this.messages[this.streamingMessageIndex].content = safeContent;
            }
            this.chatScreen.refresh();
            this.cdr.detectChanges();
            this._scrollToBottom();
          }
        }
        break;

      case 'stream-end':
        console.log('Stream ended for:', data.message_id);

        // Clear any streaming timeout
        if (this.streamingTimeoutId) {
          clearTimeout(this.streamingTimeoutId);
          this.streamingTimeoutId = null;
        }

        // Always handle stream-end even if message ID doesn't match
        // This ensures we clean up properly in all cases
        if (this.streamingMessageId !== data.message_id) {
          console.log('Message ID mismatch on stream-end, but handling anyway');
        }

        // Update message to indicate streaming is complete
        if (this.streamingMessageIndex >= 0) {
          // When streaming is complete, extract LINKS and convert to regular string for markdown
          const { content: finalContent, links } = ChatScreenComponent.extractLinks(this.streamingContent);
          this.messages[this.streamingMessageIndex].content = finalContent;
          if (links.length) this.messages[this.streamingMessageIndex].links = links;
          this.messages[this.streamingMessageIndex].isStreaming = false;
        }

        // Reset streaming state
        this.streamingMessageId = null;
        this.streamingContent = '';
        this.streamingMessageIndex = -1;

        // CRITICAL: Always reset UI state flags
        this.loading = false;
        this.chatActive = false;
        this.activeChatId = null;

        // Update sessions to refresh the list
        this.updateSessions();

        // Force change detection and UI updates
        this.chatScreen.refresh();
        this.cdr.detectChanges();
        this._scrollToBottom();
        this._focusPromptBox();
        this._backfillMessageIds();
        break;

      case 'stream-error':
        console.error('Streaming error:', data.error);

        // Clear any streaming timeout
        if (this.streamingTimeoutId) {
          clearTimeout(this.streamingTimeoutId);
          this.streamingTimeoutId = null;
        }

        // User-initiated cancel: just stop streaming, no error banner
        if (data.error && typeof data.error === 'string' && data.error.includes('Cancelled by user')) {
          if (this.streamingMessageIndex >= 0) {
            this.messages[this.streamingMessageIndex].isStreaming = false;
          }
        } else {
          // Add error message to the chat
          this.messages.push({
            isSelf: false,
            content: `Error: ${data.error || 'An error occurred during streaming'}`,
            isError: true
          });
        }

        // Force UI reset
        this.loading = false;
        this.chatActive = false;
        this.activeChatId = null;
        this.streamingMessageId = null;
        this.streamingContent = '';
        this.streamingMessageIndex = -1;

        // Force change detection
        this.cdr.detectChanges();
        this._scrollToBottom();
        this._focusPromptBox();
        break;

      // Handle case where we don't get a stream-end event but tokens stop coming
      // This special case helps handle situations where the backend fails to send stream-end
      default:
        // If we have an unknown event type and we're streaming, log it
        console.warn(`Unknown stream event type: ${data.type} for session ${data.session}`);
    }
  }

  /**
   * Strip system context tags (desktop-state, reactions) from user messages for display.
   * These are prepended by the frontend before sending but shouldn't be shown.
   */
  /**
   * Strip system context envelope from user messages for display.
   * Handles both new <context> format and legacy <desktop-state>/<reactions> tags.
   */
  private _stripSystemContext(content: string): string {
    if (!content || typeof content !== 'string') return content;
    return content
      .replace(/<context>[\s\S]*?<\/context>\s*/g, '')
      .replace(/<desktop-state>[\s\S]*?<\/desktop-state>\s*/g, '')
      .replace(/<reactions>[\s\S]*?<\/reactions>\s*/g, '')
      .trim();
  }

  /**
   * Build a unified context envelope and prepend it to the user's message.
   * Single structured JSON blob that carries all session context.
   */
  private _buildContextEnvelope(userMessage: string): string {
    const envelope: any = {
      trigger: {
        type: 'user_message',
        session_id: this.uuid
      }
    };

    // Desktop state
    const desktop = this._desktopState.toJSON();
    if (desktop) {
      envelope.desktop = desktop;
    }

    // Widget context (what apps are showing)
    const widgetContexts = this._widgetContext.getContexts();
    if (widgetContexts.length > 0) {
      envelope.widgets = widgetContexts;
    }

    // Reactions + recent message IDs (only when reactions lab is enabled)
    if (this._settings.labsReactions) {
      const recentWithIds = this.messages.slice(-10).filter(m => m.messageId);
      envelope.messages = recentWithIds.map(m => {
        const entry: any = {
          id: m.messageId,
          role: m.isSelf ? 'user' : 'assistant',
          preview: typeof m.content === 'string'
            ? m.content.substring(0, 80).replace(/\n/g, ' ')
            : ''
        };
        if (m.reactions?.length) {
          entry.reactions = m.reactions.map((r: any) => `${r.emoji}(${r.reactor})`);
        }
        return entry;
      });

    }

    const contextBlock = `<context>\n${JSON.stringify(envelope, null, 2)}\n</context>`;
    return `${contextBlock}\n\n${userMessage}`;
  }

  /**
   * Connect to the telemetry WebSocket for real-time reaction updates.
   */
  private _connectTelemetryWs(): void {
    if (this._telemetryWs?.readyState === WebSocket.OPEN) return;

    const muxUrl = ConfigService.muxUrl;
    let wsUrl: string;
    if (muxUrl.startsWith('http://') || muxUrl.startsWith('https://')) {
      const wsProtocol = muxUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = muxUrl.replace(/^https?:\/\//, '');
      wsUrl = `${wsProtocol}://${wsHost}/ws/telemetry`;
    } else {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsPath = muxUrl.startsWith('/') ? muxUrl : `/${muxUrl}`;
      wsUrl = `${wsProtocol}://${window.location.host}${wsPath}/ws/telemetry`;
    }

    this._telemetryWs = new WebSocket(wsUrl);
    this._telemetryWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'telemetry_batch' && data.events) {
          for (const evt of data.events) {
            if (evt.type === 'reaction' && evt.session_id === this.uuid) {
              this._handleReactionEvent(evt);
            }
          }
        }
      } catch (e) { /* ignore parse errors */ }
    };
    this._telemetryWs.onerror = () => {};
    this._telemetryWs.onclose = () => { this._telemetryWs = null; };
  }

  private _disconnectTelemetryWs(): void {
    if (this._telemetryWs) {
      this._telemetryWs.close();
      this._telemetryWs = null;
    }
  }

  /**
   * Handle an incoming reaction event from the telemetry WebSocket.
   */
  private _handleReactionEvent(data: any): void {
    const msg = this.messages.find(m => m.messageId === data.message_id);
    if (msg) {
      msg.reactions = data.reactions;
      this.cdr.detectChanges();
    }
  }

  /**
   * Backfill messageId on recently-sent messages by fetching the latest from the server.
   * Messages created during streaming don't have server-assigned IDs, which breaks pinning.
   */
  private async _backfillMessageIds(): Promise<void> {
    if (!this.uuid) return;
    try {
      const result = await this._muxSession.loadSession(this.uuid, 2, 0, this.currentAgentId);
      // loadSession returns newest first — match them to our in-memory messages by role
      for (const serverMsg of result.messages) {
        // Walk backwards through messages to find matching unidentified message
        for (let i = this.messages.length - 1; i >= 0; i--) {
          const localMsg = this.messages[i];
          if (localMsg.messageId) continue; // already has an ID
          const localRole = localMsg.isSelf ? 'user' : 'assistant';
          if (localRole === serverMsg.role) {
            localMsg.messageId = serverMsg.id;
            localMsg.pinned = serverMsg.pinned;
            break;
          }
        }
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.warn('Failed to backfill message IDs:', error);
    }
  }
}
